// Ultimate Card Game (UCG) - HUD & Interface Manager
//
// Neustart "Marine Null": cards can no longer be discarded (drag-down and
// the standalone discard button are gone), the Research Lab free-swap
// button is gone along with the building itself, the building-info popup
// and Intel Uplink panel are gone (buildings are unreachable with only
// Marine enabled), and minerals/CO-Power are gone from the resource
// dashboard - plasma is the only resource. An empty hand slot is now a
// buy button (DRAW command) instead of a passive "drawing..." timer
// readout. See NEUSTART_PLAN.html Phase 3/4.
import { AvatarRenderer } from './avatarRenderer.js';
import { computeLiveGamerankProjection, gradeFromComposite } from '../engine/gamerank.js';
import { GAMERANK, BALANCE } from '../data/balance.js';
import { inventory } from '../engine/inventory.js';
import { getGoldCardImage, getCardByName } from '../data/cards.js';
import { getCardThumbUrl } from '../data/cardThumbUrl.js';
import { getIconSvg } from './icons.js';

// Hacker hand-reveal tiles are 85x118 css px (not hand-card sized): the 224 px thumb tier is plenty.
const HACKER_REVEAL_THUMB_SIZE = 's';

// How long the low-graphics suggestion bubble (showLowGraphicsHint(), fired
// from PerformanceManager the first time auto-tuning would otherwise drop
// the player to 'low' quality) stays up before self-dismissing.
const GRAPHICS_HINT_VISIBLE_MS = 7000;

const _casteClassCache = new Map();

// Drag-to-play tuning (see buildHandDOM's per-card pointer wiring and
// beginCardDrag/updateCardDrag/resolveCardDrag below). Distances are in
// CSS px of vertical pointer travel from where the press started.
const CARD_DRAG_ARM_MS = 130; // hold-still time before a card "lifts"
const CARD_DRAG_ARM_MOVE_PX = 8; // OR this much movement, whichever first
const CARD_PLAY_THRESHOLD_PX = -70; // dragged up this far -> plays on release

// .card-slot's authored design box (see css/cards.css). Every on-screen
// card size in this file is this box times a scale factor, never a
// re-layout at a different size, so a zoomed card is pixel-for-pixel the
// same card the player sees in the hand - just bigger.
const CARD_DESIGN_W = 155;
const CARD_DESIGN_H = 228;

// How long the melt convergence's watchdog waits for a real frame before
// assuming requestAnimationFrame has stalled and recovering on its own -
// see HUD._armMeltWatchdog(). Frames arrive far more often than this in
// normal play (see _scrubMeltConvergence()), so this only ever fires on a
// genuine stall, never on ordinary frame jitter.
const MELT_WATCHDOG_MS = 1500;

// Safety valve for the Deploy Window (see _enterDeployWindow below). That
// window normally closes by itself the moment the PLAY command resolves
// and the card leaves the hand - next frame offline, ~one WORLD_STATE
// broadcast (~33ms) plus RTT later in an online match, or immediately on a
// CMD_REJECTED (see js/main.js's wsClient.onCmdRejected). If that never
// happens at all - the connection stalls, a rejection message gets lost -
// the slot would stay locked and its cost would stay subtracted from the
// plasma readout forever, so give up on it after this long instead. Kept
// short: this is a lost-packet fallback now, not the primary unlock path.
const DEPLOY_WINDOW_MAX_MS = 1000;


// Card inspection ("read the rules text"), Hearthstone-style: a desktop
// mouse HOVERS a hand card to blow it up beside the hand; touch has no
// hover, so there the press-and-hold that already lifts the drag preview
// lifts it at this same blown-up size instead (see beginCardDrag). Hand
// cards render at scale 0.45-0.5 on phones, i.e. ~4px rules text - the
// whole point of this view is that it is the one place the text is
// actually legible.
const CARD_ZOOM_MAX_SCALE = 2.1; // ceiling on roomy desktop viewports
// The desktop hover reads at three quarters of that ceiling. Touch needs
// every pixel it can get (the finger is holding a card that renders at
// scale 0.45-0.5 in the hand), but a mouse user is looking at an unscaled
// 155x228 card to begin with, so the full 2.1x is more overlay than the
// job needs.
const CARD_ZOOM_HOVER_SCALE_FACTOR = 0.75;
const CARD_ZOOM_VH_SHARE = 0.86; // ...but never taller than this much of the viewport
// ...nor wider than this much of it. Generous on purpose: this only ever
// binds on a narrow (portrait phone) viewport, which is exactly where the
// hand card is smallest and the zoom is needed most - CARD_ZOOM_MAX_SCALE
// is what keeps a desktop card from ballooning, not this.
const CARD_ZOOM_VW_SHARE = 0.82;
// Hover dwell before the zoom appears. Long on purpose: the zoom covers a
// good part of the battlefield, so it may not fire while the mouse is
// merely passing over the hand on its way somewhere else. The clock runs
// per HAND VISIT, not per card - see _scheduleCardZoom(): sliding along
// the row from card to card keeps counting, so the wait is paid once and
// every card after that reads instantly.
const CARD_ZOOM_HOVER_DELAY_MS = 500;
const CARD_ZOOM_ANCHOR_GAP_PX = 12; // hover: gap between the hand card's top edge and the zoom
const CARD_ZOOM_POINTER_GAP_PX = 26; // touch: gap between the finger and the lifted card
const CARD_ZOOM_VIEWPORT_MARGIN_PX = 6; // keeps the zoom fully on screen at any anchor

export class HUD {
  constructor(game) {
    this.game = game;
    this.lastHandSignature = '';
    this._handDirty = false;
    const initialHandSize = BALANCE?.maxHandSize || 5;
    this._lastHandCardIds = new Array(initialHandSize).fill(null);
    this._lastHandCardInstances = new Array(initialHandSize).fill(null);
    this.lastSeenPlayErrorSeq = 0;
    this.lastSeenDrawErrorSeq = 0;
    this.lastSeenConcentrationSeq = 0;
    this.lastSeenMentalExtractionSeq = 0;
    this._concentrationSuctionActive = false;
    this._concentrationSuctionStart = 0;
    this._cachedHandSlots = [];
    this.avatarRenderer = new AvatarRenderer(game);
    this.initElements();
  }

  // Cutscene support (docs/game-design-singleplayer-kampagne.md Kapitel 6):
  // fades the top resource bar and bottom card/control chrome out of view
  // without touching #canvas-container, so the battlefield canvas itself
  // (where the cutscene plays) stays fully visible. No prior generic
  // whole-HUD toggle existed - OnboardingGuide only ever shows/hides its
  // own bubble overlay, never this chrome.
  setVisible(visible) {
    for (const id of ['top-hud', 'bottom-hud']) {
      const el = document.getElementById(id);
      if (!el) continue;
      el.style.transition = 'opacity 0.3s ease';
      el.style.opacity = visible ? '1' : '0';
      el.style.pointerEvents = visible ? '' : 'none';
    }
    if (visible) {
      const sidebar = document.getElementById('sidebar-controls');
      if (sidebar) void sidebar.offsetHeight;
    }
  }

  // Reduced campaign HUD (docs/singleplayer implementation soft-foraging-
  // flamingo.md): #top-hud/#bottom-hud (plasma vessel, hand tray, sidebar
  // buttons) stay hidden via setVisible(false) from the intro cutscene's
  // own `hud` step - this only ever adds a top-left menu toggle and the
  // one playable card by itself on top of the fullscreen. Only ever called
  // from js/main.js's campaign flow (armed-but-not-played -> here; card
  // played -> setCampaignCinematicMode(false) + setVisible(true) for the
  // real fight).
  setCampaignCinematicMode(active) {
    const overlay = document.getElementById('campaign-cinematic-hud');
    const cardSlot = document.getElementById('campaign-cinematic-card-slot');
    if (!overlay || !cardSlot) return;

    if (!active) {
      overlay.classList.add('hidden');
      cardSlot.innerHTML = '';
      // Rebuilds #cards-hand-container fresh from the current hand state -
      // simplest way to get the moved-out card's real node back in its
      // normal spot, no manual re-parenting bookkeeping needed.
      this.buildHandDOM();
      return;
    }

    const team = this.game.myTeam || 'western';
    const slotIndex = this.game.cardSystem.hands[team].findIndex(c => c);
    const wrapperDiv = slotIndex !== -1 ? this.elHandContainer?.children[slotIndex] : null;
    cardSlot.innerHTML = '';
    if (wrapperDiv) {
      // Re-parents the REAL hand-slot node (click listeners from
      // buildHandDOM() intact) rather than cloning - see this method's
      // header comment and the CSS scaling rule scoped to
      // #campaign-cinematic-card-slot in css/main.css.
      cardSlot.appendChild(wrapperDiv);
    }
    overlay.classList.remove('hidden');
  }

  initElements() {
    this.elWesternHp = document.getElementById('western-hp-bar');
    this.elWesternGhostHp = document.getElementById('western-ghost-hp-bar');
    this.elWesternHpText = document.getElementById('western-hp-text');
    this.elEasternHp = document.getElementById('eastern-hp-bar');
    this.elEasternGhostHp = document.getElementById('eastern-ghost-hp-bar');
    this.elEasternHpText = document.getElementById('eastern-hp-text');

    this.elWesternDecreePill = document.getElementById('western-decree-pill');
    this.elWesternDecreeName = document.getElementById('western-decree-name');
    this.elWesternDecreeEffect = document.getElementById('western-decree-effect');
    this.elWesternDecreeTimer = document.getElementById('western-decree-timer');
    this.elWesternDecreeProgress = document.getElementById('western-decree-progress');
    this.elWesternDecreeBalloon = document.getElementById('western-decree-balloon');
    this.elWesternShoutIntro = document.getElementById('western-shout-intro');
    this.elWesternShoutPunchline = document.getElementById('western-shout-punchline');

    this.elEasternDecreePill = document.getElementById('eastern-decree-pill');
    this.elEasternDecreeName = document.getElementById('eastern-decree-name');
    this.elEasternDecreeEffect = document.getElementById('eastern-decree-effect');
    this.elEasternDecreeTimer = document.getElementById('eastern-decree-timer');
    this.elEasternDecreeProgress = document.getElementById('eastern-decree-progress');
    this.elEasternDecreeBalloon = document.getElementById('eastern-decree-balloon');
    this.elEasternShoutIntro = document.getElementById('eastern-shout-intro');
    this.elEasternShoutPunchline = document.getElementById('eastern-shout-punchline');

    const hookDecreePill = (pill, team) => {
      if (!pill) return;
      const trigger = () => {
        const ms = this.game?.battlefield?.moduleSystem;
        if (ms?.triggerCongressSpeechBubble) {
          ms.triggerCongressSpeechBubble(team);
        }
      };
      pill.addEventListener('mouseenter', trigger);
      pill.addEventListener('click', (e) => {
        e.stopPropagation();
        trigger();
      });
    };
    hookDecreePill(this.elWesternDecreePill, 'western');
    hookDecreePill(this.elEasternDecreePill, 'eastern');

    this.elPlasma = document.getElementById('res-plasma');
    this.elPlasmaCur = document.getElementById('res-plasma-cur');
    this.elPlasmaCap = document.getElementById('res-plasma-cap');

    // Parent .res-item pill, for the gain-pulse/insufficient-shake juice -
    // fetched once here rather than re-querying every frame.
    this.elPlasmaItem = this.elPlasma?.closest('.res-item');
    this.elTopHud = document.getElementById('top-hud');
    this.elBottomHud = document.getElementById('bottom-hud');
    this.elScanlines = document.getElementById('battlefield-scanlines');
    this.elPlasmaCapsule = document.getElementById('plasma-capsule-container');
    this.elPlasmaFluid = document.getElementById('plasma-fluid-fill');
    this.elPlasmaSegments = (typeof document !== 'undefined' && typeof document.querySelectorAll === 'function')
      ? document.querySelectorAll('.gauge-seg')
      : [];
    this._lastActiveCount = -1;
    this._prevPlasma = null;

    // Sul plasma-gauge theme (docs/RACE_PLASMA_GAUGE_PARADIGMA.md) - own
    // number-readout DOM nodes (duplicate ids aren't valid HTML, so the
    // Human theme's #res-plasma-cur/-cap can't be reused). Chambers are
    // bound by color/class, NOT by DOM/visual order: the purple center
    // chamber sits visually in the middle socket but fills LAST (tier 2),
    // so elSulChambersByTier is [blue, green, purple] regardless of where
    // they sit on screen (see index.html/css for the visual layout).
    this.elPlasmaCurSul = document.getElementById('res-plasma-cur-sul');
    this.elPlasmaCapSul = document.getElementById('res-plasma-cap-sul');
    this.elSulChambersByTier = [
      document.querySelector('.sul-chamber.chamber-blue'),
      document.querySelector('.sul-chamber.chamber-green'),
      document.querySelector('.sul-chamber.chamber-purple'),
    ];
    this.elSulChamberFills = this.elSulChambersByTier.map((c) => c?.querySelector('.chamber-fill'));
    this._lastIsSulTheme = null;
    this._hasRenderedPausedHud = false;

    this.elHandContainer = document.getElementById('cards-hand-container');

    // Drag-to-play preview - see beginCardDrag() etc. below.
    this.elDragPreview = document.getElementById('card-drag-preview');

    // Hover-to-inspect zoom - see showCardZoom() below. Separate element
    // from the drag preview on purpose: the two never show at once (a
    // press hides the zoom), but the drag preview carries generation/
    // cleanup state across its own async animations, and threading a
    // second, differently-timed lifecycle through that is exactly how the
    // stale-cleanup bug documented in beginCardDrag() got in.
    this.elCardZoom = document.getElementById('card-zoom-preview');
    this.elCardStage = document.getElementById('card-stage');
    this._cardZoomTimer = null;

    // Live Gamerank Segmented Bar Elements (1Hz Throttled, Zero-GC View Layer)
    // The bar shows YOUR OWN Combat Performance Grade. Both a western and an
    // eastern instance exist in the markup (see index.html's
    // boss-gauge-stack.western / .eastern) because in online 1v1 matches
    // this.game.myTeam can be 'eastern' for the second player - whichever
    // side is genuinely the local human's, that side's bar goes live via
    // updateLiveGamerank() below; the other side is kept as a dimmed
    // `.placeholder` shell by js/main.js's updateBaseLabels() (never fed
    // fake data of its own).
    this.elWesternRankBar = document.getElementById('western-live-rank-bar');
    this.elWesternRankTag = document.getElementById('western-rank-live-tag');
    this.elEasternRankBar = document.getElementById('eastern-live-rank-bar');
    this.elEasternRankTag = document.getElementById('eastern-rank-live-tag');
    this.elWesternAvatar = document.querySelector('.boss-avatar.western');
    this.elEasternAvatar = document.querySelector('.boss-avatar.eastern');
    this.elLiveSRankAura = document.getElementById('live-s-rank-aura');

    this._rankBarSegs = {
      western: {
        bar: this.elWesternRankBar,
        tag: this.elWesternRankTag,
        c: this.elWesternRankBar?.querySelector('.seg-c'),
        b: this.elWesternRankBar?.querySelector('.seg-b'),
        a: this.elWesternRankBar?.querySelector('.seg-a'),
        s: this.elWesternRankBar?.querySelector('.seg-s'),
        sPlus: this.elWesternRankBar?.querySelector('.seg-s-plus'),
        fillC: this.elWesternRankBar?.querySelector('.seg-c .seg-fill'),
        fillB: this.elWesternRankBar?.querySelector('.seg-b .seg-fill'),
        fillA: this.elWesternRankBar?.querySelector('.seg-a .seg-fill'),
        fillS: this.elWesternRankBar?.querySelector('.seg-s .seg-fill'),
        fillSPlus: this.elWesternRankBar?.querySelector('.seg-s-plus .seg-fill')
      },
      eastern: {
        bar: this.elEasternRankBar,
        tag: this.elEasternRankTag,
        c: this.elEasternRankBar?.querySelector('.seg-c'),
        b: this.elEasternRankBar?.querySelector('.seg-b'),
        a: this.elEasternRankBar?.querySelector('.seg-a'),
        s: this.elEasternRankBar?.querySelector('.seg-s'),
        sPlus: this.elEasternRankBar?.querySelector('.seg-s-plus'),
        fillC: this.elEasternRankBar?.querySelector('.seg-c .seg-fill'),
        fillB: this.elEasternRankBar?.querySelector('.seg-b .seg-fill'),
        fillA: this.elEasternRankBar?.querySelector('.seg-a .seg-fill'),
        fillS: this.elEasternRankBar?.querySelector('.seg-s .seg-fill'),
        fillSPlus: this.elEasternRankBar?.querySelector('.seg-s-plus .seg-fill')
      }
    };

    this._liveRatingTimer = 0;
    this._lastLiveGrade = null;
    this._lastLiveScore = -1;
    this._lastLiveAuraActive = false;
    // Start of the current hand-hover visit, or null between visits - the
    // dwell clock that _scheduleCardZoom() measures against. Deliberately
    // NOT per card: see the constant's comment.
    this._handHoverStart = null;
    this._lastTouchTime = 0;
    this._touchActive = false;

    // Shared drag state (one tracker, not one set of closures per card)
    // plus window-level listeners added ONCE here rather than per card or
    // per buildHandDOM() rebuild - see _startCardPress() for why.
    this._cardDrag = null;
    this._suppressClickSlots = new Set();

    // Deploy Window state: slotIndex -> the card, its cost and the nodes
    // showing the "deploying" treatment, plus the running total of costs
    // those windows have optimistically subtracted from the displayed
    // plasma. Both are purely presentational - see _enterDeployWindow().
    this._deployWindowSlots = new Map();
    this._optimisticReservedPlasma = 0;
    window.addEventListener('pointermove', (e) => this._onCardDragMove(e));
    window.addEventListener('pointerup', (e) => this._onCardDragEnd(e, false));
    window.addEventListener('pointercancel', (e) => this._onCardDragEnd(e, true));

    // Suppress contextmenu on hand container, previews and stage (prevents Android Chrome image context menu on hold)
    this.elHandContainer?.addEventListener('contextmenu', (e) => e.preventDefault());
    this.elDragPreview?.addEventListener('contextmenu', (e) => e.preventDefault());
    this.elCardZoom?.addEventListener('contextmenu', (e) => e.preventDefault());
    this.elCardStage?.addEventListener('contextmenu', (e) => e.preventDefault());

    // Suppress synthetic mouse events on touch devices (e.g. iPad Safari PWA).
    // On iOS WebKit, touch interactions emit synthetic mouseenter/pointerenter
    // with pointerType: 'mouse' after touch release, leaving cards in a sticky
    // hover state because no pointerleave ever fires without a physical cursor.
    const markTouchStart = () => {
      this._lastTouchTime = performance.now();
      this._touchActive = true;
      this.endCardHoverSession();
    };
    const markTouchEnd = () => {
      this._lastTouchTime = performance.now();
      this._touchActive = false;
    };
    window.addEventListener('touchstart', markTouchStart, { passive: true, capture: true });
    window.addEventListener('touchend', markTouchEnd, { passive: true, capture: true });
    window.addEventListener('touchcancel', markTouchEnd, { passive: true, capture: true });
    window.addEventListener('pointerdown', (e) => {
      if (e.pointerType !== 'mouse') markTouchStart();
    }, { passive: true, capture: true });

    const setupBackdrop = document.getElementById('setup-sheet-backdrop');
    if (setupBackdrop) {
      setupBackdrop.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.closeSetupSheet();
      });
      setupBackdrop.addEventListener('pointerdown', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.closeSetupSheet();
      });
    }

    this._bindSetupSheetSwipe();

    // Hamburger: opens #setup-controls (Decks/Online/Sound/AI/Pause/Speed/
    // Grafik) as a slide-in sheet - universal now, not just mobile, see
    // css/main.css's base #setup-controls/.mobile-menu-toggle rules
    // (portrait overrides the anchor to a bottom sheet instead).
    document.getElementById('btn-mobile-menu')?.addEventListener('click', () => this.toggleSetupSheet());

    // Campaign cinematic HUD's own top-left menu toggle (docs/singleplayer
    // implementation soft-foraging-flamingo.md) - opens the exact same
    // #setup-controls sheet as the normal hamburger above, just from a
    // visually-relocated button, since toggleSetupSheet() itself positions
    // the sheet purely via CSS (see css/main.css), never off the button's
    // own on-screen position.
    document.getElementById('campaign-cinematic-menu-btn')?.addEventListener('click', () => this.toggleSetupSheet());

    // Sizes the mobile-portrait hand to whatever width the hand row
    // actually has - see the .card-wrapper/.card-slot portrait rules in
    // css/cards.css, which read the --hand-card-* custom properties this
    // sets instead of a fixed px size. A fixed size tuned on one test
    // device can quietly overflow into horizontal scroll on a narrower
    // real phone (5 cards near the width limit leaves ~0 tolerance - see
    // chat report of clipped cards on an iPhone) - recomputing the exact
    // size that fits BALANCE.maxHandSize cards side by side, every time
    // the hand row's own box changes size, means it can only ever fit or
    // shrink, never overflow. Same ResizeObserver-driven pattern as
    // js/ui/viewport.js's canvas resize handling, for the same reason:
    // it catches layout changes regardless of what caused them, without
    // depending on a window 'resize' event actually firing.
    this.updateHandCardScale();
    if (this.elHandContainer && typeof ResizeObserver !== 'undefined') {
      this._handResizeObserver = new ResizeObserver(() => this.updateHandCardScale());
      this._handResizeObserver.observe(this.elHandContainer);
    }
    window.addEventListener('resize', () => {
      this._hasRenderedPausedHud = false;
      this.updateHandCardScale();
      const sidebar = document.getElementById('sidebar-controls');
      if (sidebar) void sidebar.offsetHeight;
    });
    window.addEventListener('orientationchange', () => {
      this._hasRenderedPausedHud = false;
      this.updateHandCardScale();
      const sidebar = document.getElementById('sidebar-controls');
      if (sidebar) void sidebar.offsetHeight;
    });

    // The zoom is placed in viewport coordinates against an anchor it does
    // not own (a hand card, or the pointer), so anything that can move that
    // anchor out from under it has to dismiss it rather than leave it
    // floating over an empty spot.
    window.addEventListener('resize', () => this.endCardHoverSession());
    window.addEventListener('orientationchange', () => this.endCardHoverSession());
    window.addEventListener('blur', () => this.endCardHoverSession());

    // Leaving the hand row - not merely leaving one card - is what ends a
    // hover visit and puts the dwell back to the full wait. pointerleave
    // on the container fires only when the pointer leaves the container
    // itself, never when it crosses between the cards inside it, which is
    // exactly the boundary the dwell clock is meant to track.
    this.elHandContainer?.addEventListener('pointerleave', () => {
      this.endCardHoverSession();
    });

    // Warm up and decode melt card textures so their first appearance is instant without micro-stutter
    if (typeof Image !== 'undefined') {
      ['1_Hypermarine.png', '1_hypershade.png'].forEach(src => {
        const img = new Image();
        img.src = getCardThumbUrl(src);
        if (img.decode) img.decode().catch(() => {});
      });
    }

    // Human Hacker Intel HUD (Opponent Plasma Capsule & Revealed Hand)
    this._initHackerIntelElements();
  }

  updateHandCardScale() {
    const container = this.elHandContainer;
    if (!container) return;

    // buildHandDOM() calls this on every single hand-content change (draw/
    // play/melt - see its own comment), which in an online match with
    // auto-draw fires roughly every 350-600ms. The container's box only
    // actually changes size on a real resize/orientation change (wired via
    // ResizeObserver/window listeners in initElements()), never just because
    // a card was drawn - so skip the --hand-card-* custom-property writes on
    // documentElement (a style recalc of the whole document) when nothing
    // they depend on has moved. This was the dominant cause of the periodic
    // ~200ms main-thread stalls reported as online netcode "jitter"
    // (js/net/stateStream.js jitterMs is just measuring how late this same
    // thread got around to reading the next WORLD_STATE off the WebSocket).
    // clientWidth is read first: it settles layout (and with it style), so
    // the getComputedStyle() reads after it cost nothing extra.
    const clientWidth = container.clientWidth;

    // Two layouts consume the result: portrait phones (the defaults below)
    // and the narrow-desktop band in css/cards.css (tablets, unfolded
    // foldables), which announces its own ceiling and wrapper padding
    // through --hand-fit-*. Part of the cache key, so switching between
    // layouts at an unchanged width is never skipped as "nothing changed".
    if (typeof window === 'undefined' || typeof window.getComputedStyle !== 'function') return;
    const style = window.getComputedStyle(container);
    const MAX_SCALE = parseFloat(style.getPropertyValue('--hand-fit-max-scale')) || 0.5; // the size cards grow to when there's room to spare
    const fitPadTop = parseFloat(style.getPropertyValue('--hand-fit-pad-top'));
    const PADDING_TOP = Number.isFinite(fitPadTop) ? fitPadTop : 8; // matches .card-wrapper's portrait padding-top
    const GAP = parseFloat(style.columnGap) || 4; // #cards-hand-container's gap (portrait 4px, desktop 12px)
    const HAND_SIZE = 5; // BALANCE.maxHandSize
    const SAFETY_PX = 4; // absorbs sub-pixel layout rounding so this can only under-fit, never overflow

    const scaleKey = `${clientWidth}|${MAX_SCALE}|${PADDING_TOP}|${GAP}`;
    if (scaleKey === this._lastHandScaleWidth) return;
    this._lastHandScaleWidth = scaleKey;

    const padLeft = parseFloat(style.paddingLeft) || 0;
    const padRight = parseFloat(style.paddingRight) || 0;
    const available = Math.max(0, clientWidth - padLeft - padRight);
    if (available <= 0) {
      this._lastHandScaleWidth = undefined; // not laid out yet - retry next call instead of latching a bogus 0/negative width
      return; // keep the CSS fallback until a real measurement comes in
    }

    const rawWidth = (available - GAP * (HAND_SIZE - 1) - SAFETY_PX) / HAND_SIZE;
    const cardW = Math.max(0, Math.min(MAX_SCALE * CARD_DESIGN_W, Math.floor(rawWidth)));
    const scale = cardW / CARD_DESIGN_W;
    const cardH = Math.round(CARD_DESIGN_H * scale) + PADDING_TOP;

    const root = document.documentElement.style;
    root.setProperty('--hand-card-scale', scale.toFixed(4));
    root.setProperty('--hand-card-active-scale', (scale * 0.98).toFixed(4));
    root.setProperty('--hand-card-w', `${cardW}px`);
    root.setProperty('--hand-card-h', `${cardH}px`);
  }

  toggleSetupSheet() {
    const panel = document.getElementById('setup-controls');
    const backdrop = document.getElementById('setup-sheet-backdrop');
    if (!panel) return;
    const opening = !panel.classList.contains('open');
    panel.classList.toggle('open', opening);
    backdrop?.classList.toggle('open', opening);
    // Opening the menu is exactly what the hint was pointing at - once
    // they're looking at the Graphics selector it's served its purpose.
    if (opening) {
      this.clearPerfMenuNotice();
      this._hideLowGraphicsHint();
      this._hideAiDifficultyHint?.();
      const btnAiHand = document.getElementById('btn-toggle-ai-hand');
      if (btnAiHand) {
        const isVisible = !!this.game?.showAiHand;
        btnAiHand.innerText = isVisible ? 'AI Hand: ON' : 'AI Hand: OFF';
        btnAiHand.classList.toggle('active', isVisible);
      }
      this.game?.pauseForMenu?.('setupControls');
    } else {
      this.game?.resumeForMenu?.('setupControls');
    }
  }

  // Drag-down-to-dismiss on the portrait bottom sheet's grab handle
  // (#setup-drag-area, only displayed in the portrait media query). The sheet
  // tracks the finger through --drag-translate (css: #setup-controls.open.dragging);
  // releasing past ~30% of its height (max 140px) closes it, otherwise it
  // snaps back. Mirrors the deck drawer in deckBuilderModal.js.
  _bindSetupSheetSwipe() {
    const handle = document.getElementById('setup-drag-area');
    const panel = document.getElementById('setup-controls');
    if (!handle || !panel) return;
    let startY = 0;
    let deltaY = 0;
    let dragging = false;

    const onStart = (e) => {
      if (!e.touches?.length) return;
      startY = e.touches[0].clientY;
      deltaY = 0;
      dragging = true;
      panel.classList.add('dragging');
      handle.classList.add('dragging');
    };
    const onMove = (e) => {
      if (!dragging || !e.touches?.length) return;
      deltaY = Math.max(0, e.touches[0].clientY - startY);
      panel.style.setProperty('--drag-translate', `${deltaY}px`);
    };
    const onEnd = () => {
      if (!dragging) return;
      dragging = false;
      panel.classList.remove('dragging');
      handle.classList.remove('dragging');
      const shouldClose = deltaY > Math.min(140, panel.offsetHeight * 0.3);
      panel.style.removeProperty('--drag-translate');
      deltaY = 0;
      if (shouldClose) this.closeSetupSheet();
    };

    handle.addEventListener('touchstart', onStart, { passive: true });
    handle.addEventListener('touchmove', onMove, { passive: true });
    handle.addEventListener('touchend', onEnd, { passive: true });
    handle.addEventListener('touchcancel', onEnd, { passive: true });
  }

  openSetupSheet() {
    const panel = document.getElementById('setup-controls');
    if (!panel || panel.classList.contains('open')) return;
    this.toggleSetupSheet();
  }

  closeSetupSheet() {
    const panel = document.getElementById('setup-controls');
    const wasOpen = panel?.classList.contains('open');
    panel?.classList.remove('open');
    document.getElementById('setup-sheet-backdrop')?.classList.remove('open');
    this.clearPerfMenuNotice();
    this._hideLowGraphicsHint?.();
    this._hideAiDifficultyHint?.();
    if (wasOpen) {
      this.game?.audio?.playUiClick?.();
      this.game?.resumeForMenu?.('setupControls');
    }
  }

  isSetupSheetOpen() {
    return document.getElementById('setup-controls')?.classList.contains('open') || false;
  }

  showPerfMenuNotice() {
    if (typeof document === 'undefined') return;
    const btn = document.getElementById('btn-mobile-menu');
    btn?.classList.add('has-perf-notice');
  }

  clearPerfMenuNotice() {
    if (typeof document === 'undefined') return;
    const btn = document.getElementById('btn-mobile-menu');
    btn?.classList.remove('has-perf-notice');
  }

  // Nudged from PerformanceManager._notifyLowGraphicsSuggested() the one
  // time auto-tuning would otherwise silently drop the player all the way
  // to 'low' quality: a self-dismissing bubble on the menu button pointing
  // at the manual Graphics selector, instead of switching it out from under
  // them. Positioned purely from the button's live getBoundingClientRect()
  // (same approach as js/ui/onboardingGuide.js's bubble) rather than fixed
  // CSS offsets, since #btn-mobile-menu's on-screen spot changes across the
  // desktop/mobile-landscape/mobile-portrait breakpoints (css/main.css's
  // #sidebar-controls rules) - this way it just works in all of them,
  // including after a live orientation change.
  //
  // Returns whether it actually showed, so the caller only latches its
  // "shown once" flag on a real show.
  showLowGraphicsHint() {
    if (typeof document === 'undefined') return false;
    const anchor = document.getElementById('btn-mobile-menu');
    if (!anchor) return false;

    let bubble = this._graphicsHintEl;
    if (!bubble) {
      bubble = document.createElement('div');
      bubble.id = 'graphics-hint-bubble';
      bubble.className = 'graphics-hint-bubble';
      bubble.innerHTML = `
        <div class="graphics-hint-bubble-inner">Set Graphics to low in Menu</div>
        <span class="bubble-chevron" aria-hidden="true"></span>
      `;
      (document.getElementById('game-wrapper') || document.body).appendChild(bubble);
      this._graphicsHintEl = bubble;
    }

    this._positionGraphicsHint();
    if (!this._boundPositionGraphicsHint) {
      this._boundPositionGraphicsHint = () => this._positionGraphicsHint();
    }
    window.addEventListener('resize', this._boundPositionGraphicsHint);
    window.addEventListener('orientationchange', this._boundPositionGraphicsHint);

    // Restart the entrance transition even if a stale instance is somehow
    // still around (defensive - showLowGraphicsHint() is only ever called
    // once per PerformanceManager instance in practice).
    bubble.classList.remove('active');
    void bubble.offsetWidth;
    bubble.classList.add('active');

    clearTimeout(this._graphicsHintTimer);
    this._graphicsHintTimer = setTimeout(() => this._hideLowGraphicsHint(), GRAPHICS_HINT_VISIBLE_MS);
    return true;
  }

  _positionGraphicsHint() {
    const bubble = this._graphicsHintEl;
    const anchor = document.getElementById('btn-mobile-menu');
    if (!bubble || !anchor) return;
    const rect = anchor.getBoundingClientRect();
    const bubbleWidth = bubble.offsetWidth || 200;
    const anchorCenterX = rect.left + rect.width / 2;
    // Centered above the button, clamped so it can't run off either edge -
    // the button sits flush against the right/bottom edge on several
    // breakpoints, where an unclamped centered bubble would clip.
    const left = Math.min(
      Math.max(8, anchorCenterX - bubbleWidth / 2),
      window.innerWidth - bubbleWidth - 8
    );
    bubble.style.left = `${Math.round(left)}px`;
    bubble.style.top = `${Math.round(rect.top - bubble.offsetHeight - 10)}px`;

    // The CSS default centers the chevron on the bubble itself (left: 50%),
    // which only points at the anchor when the bubble above didn't need
    // clamping. Near a screen edge (e.g. #btn-mobile-menu on a narrow phone)
    // the bubble shifts left but a bubble-centered chevron doesn't follow -
    // it ends up pointing at whichever neighboring button (e.g. the 4x
    // speed toggle) happens to sit under the bubble's midpoint instead. Pin
    // the chevron to the anchor's actual center, clamped to stay within the
    // bubble's own clipped-corner shape.
    const chevron = bubble.querySelector('.bubble-chevron');
    if (chevron) {
      const chevronWidth = chevron.offsetWidth || 9;
      const margin = 10;
      const chevronLeft = Math.min(
        Math.max(margin, anchorCenterX - left - chevronWidth / 2),
        Math.max(margin, bubbleWidth - margin - chevronWidth)
      );
      chevron.style.left = `${Math.round(chevronLeft)}px`;
      chevron.style.marginLeft = '0';
    }
  }

  _hideLowGraphicsHint() {
    clearTimeout(this._graphicsHintTimer);
    this._graphicsHintTimer = null;
    if (this._boundPositionGraphicsHint) {
      window.removeEventListener('resize', this._boundPositionGraphicsHint);
      window.removeEventListener('orientationchange', this._boundPositionGraphicsHint);
    }
    this._graphicsHintEl?.classList.remove('active');
  }

  // Prompted after 3 consecutive singleplayer losses (or wins) against the AI: a bubble
  // pointing at the menu hamburger ("Change AI Mode" / "Increase AI Difficulty"), analogous to showLowGraphicsHint().
  showAiDifficultyHint() {
    const options = arguments[0] || {};
    if (typeof document === 'undefined') return false;
    const anchor = document.getElementById('btn-mobile-menu');
    if (!anchor) return false;

    const isPromote = Boolean(options.isPromote);
    const text = options.text || (isPromote ? 'Increase AI Difficulty' : 'Change AI Mode');

    let bubble = this._aiDifficultyHintEl;
    if (!bubble) {
      bubble = document.createElement('div');
      bubble.id = 'ai-difficulty-hint-bubble';
      bubble.className = isPromote ? 'graphics-hint-bubble promote' : 'graphics-hint-bubble';
      bubble.style.pointerEvents = 'auto';
      bubble.style.cursor = 'pointer';
      bubble.innerHTML = `
        <div class="graphics-hint-bubble-inner">${text}</div>
        <span class="bubble-chevron" aria-hidden="true"></span>
      `;
      bubble.addEventListener('click', () => {
        this._hideAiDifficultyHint();
        const panel = document.getElementById('setup-controls');
        if (panel && !panel.classList.contains('open')) {
          this.toggleSetupSheet();
        }
      });
      (document.getElementById('game-wrapper') || document.body).appendChild(bubble);
      this._aiDifficultyHintEl = bubble;
    } else {
      bubble.classList.toggle('promote', isPromote);
      const inner = bubble.querySelector('.graphics-hint-bubble-inner');
      if (inner) inner.textContent = text;
    }

    this._positionAiDifficultyHint();
    if (!this._boundPositionAiDifficultyHint) {
      this._boundPositionAiDifficultyHint = () => this._positionAiDifficultyHint();
    }
    window.addEventListener('resize', this._boundPositionAiDifficultyHint);
    window.addEventListener('orientationchange', this._boundPositionAiDifficultyHint);

    bubble.classList.remove('active');
    void bubble.offsetWidth;
    bubble.classList.add('active');

    clearTimeout(this._aiDifficultyHintTimer);
    this._aiDifficultyHintTimer = setTimeout(() => this._hideAiDifficultyHint(), GRAPHICS_HINT_VISIBLE_MS);
    return true;
  }

  _positionAiDifficultyHint() {
    const bubble = this._aiDifficultyHintEl;
    const anchor = document.getElementById('btn-mobile-menu');
    if (!bubble || !anchor) return;
    const rect = anchor.getBoundingClientRect();
    const bubbleWidth = bubble.offsetWidth || 200;
    const anchorCenterX = rect.left + rect.width / 2;
    const left = Math.min(
      Math.max(8, anchorCenterX - bubbleWidth / 2),
      window.innerWidth - bubbleWidth - 8
    );
    bubble.style.left = `${Math.round(left)}px`;
    bubble.style.top = `${Math.round(rect.top - bubble.offsetHeight - 10)}px`;

    const chevron = bubble.querySelector('.bubble-chevron');
    if (chevron) {
      const chevronWidth = chevron.offsetWidth || 9;
      const margin = 10;
      const chevronLeft = Math.min(
        Math.max(margin, anchorCenterX - left - chevronWidth / 2),
        Math.max(margin, bubbleWidth - margin - chevronWidth)
      );
      chevron.style.left = `${Math.round(chevronLeft)}px`;
      chevron.style.marginLeft = '0';
    }
  }

  _hideAiDifficultyHint() {
    clearTimeout(this._aiDifficultyHintTimer);
    this._aiDifficultyHintTimer = null;
    if (this._boundPositionAiDifficultyHint) {
      window.removeEventListener('resize', this._boundPositionAiDifficultyHint);
      window.removeEventListener('orientationchange', this._boundPositionAiDifficultyHint);
    }
    this._aiDifficultyHintEl?.classList.remove('active');
  }

  // Caste-coded cost badge (css/cards.css's .card-slot.caste-* rules) - the
  // one card element that stays visible at every hand-card size, down to
  // the stripped-bare mobile reduction (see HAND-CARD REDUCTION), so it
  // carries the caste identity that a hover-only tooltip can't. Symbiosis
  // -tier results (the Vulture caste's own fusion target) carry no `caste`
  // of their own - detected the same way CardSystem.checkSymbiosis() finds
  // them for its candidate pool (js/engine/cardSystem.js).
  _getCardThemeClasses() {
    const themeId = inventory?.getEquippedCardTheme ? inventory.getEquippedCardTheme() : 'glass';
    if (themeId === 'holo') {
      return 'theme-glass glass-chassis glass-active style-crystal cut-topography frame-carbon mat-holo';
    }
    if (themeId === 'fluted') {
      return 'theme-glass glass-chassis glass-active style-crystal cut-micro-fluted frame-carbon mat-crystal';
    }
    if (themeId === 'glass') {
      return 'theme-glass glass-chassis glass-active style-crystal cut-knurled frame-carbon mat-crystal';
    }
    if (themeId === 'artwork') {
      return 'theme-artwork';
    }
    return 'theme-classic';
  }

  _getCasteClass(card) {
    if (!card) return '';
    const key = card.name || card.id || '';
    if (key && _casteClassCache.has(key)) {
      return _casteClassCache.get(key);
    }
    let res = '';
    if (card.caste) {
      res = `caste-${card.caste.toLowerCase()}`;
    } else if (card.race === 'Sul' && (card.type === 'Symbiosis' || card.isSymbiosisResult)) {
      res = 'caste-symbiosis';
    } else if (card.race === 'Sul') {
      res = 'race-sul-plasma';
    } else if (card.race !== 'Sul') {
      if (card.type === 'Melt' || card.isMelt || card.name?.startsWith('Hyper ')) {
        if (key) _casteClassCache.set(key, 'caste-hyper');
        return 'caste-hyper';
      }
      if (card.name) {
        const target = getCardByName(`hyper ${card.name.toLowerCase()}`);
        if (target && target.type === 'Melt') {
          if (key) _casteClassCache.set(key, 'caste-hyper');
          return 'caste-hyper';
        }
      }
    }
    if (key) _casteClassCache.set(key, res);
    return res;
  }

  // Fusion progress (1..3 segments) for the ring drawn inside the hand-card cost
  // badge disc: lights up per same-name card held in hand for fusion-capable
  // cards (Human cards with Hyper version, Sul caste units with next tier).
  _getCasteProgress(card) {
    if (!card) return null;
    const info = this.game?.cardSystem?.getFusionInfo?.(card, this.game?.myTeam);
    if (!info) return null;
    return {
      segments: Math.min(3, info.have || 0),
      kind: info.kind
    };
  }

  // 3 filled pie-slice wedges sharing .card-cost's own disc (chat request
  // "nicht außen am kreis anliegen, sondern innen im kreis (kuchenecken)" -
  // moved from a ring hugging the outside of the badge to wedges living
  // inside it). Plain SVG paths, not a CSS conic-gradient: a donut sector
  // with a true rounded-corner-free wedge shape and a real gap between
  // slices needs an actual path per slice. A small unlit hole stays cut
  // out of the centre (rInner) so the cost digit rendered on top - see
  // buildCardInnerHTML(), which nests this inside the same <span> right
  // before the digit - never sits on a same-coloured wedge at every one of
  // its pixels; radii are tuned to exactly fill .card-cost's own circle
  // (viewBox 0 0 34 34, badge diameter maps 1:1 onto it), not to sit
  // outside it.
  _buildCasteRingHtml(progress) {
    if (!progress) return '';
    const cx = 17, cy = 17;
    const rOuter = 16.5;
    const rInner = 6;
    const gapDeg = 8;
    const segDeg = 120 - gapDeg;
    const toRad = deg => (deg * Math.PI) / 180;
    const pt = (r, deg) => [cx + r * Math.cos(toRad(deg)), cy + r * Math.sin(toRad(deg))];
    const wedges = [0, 1, 2].map(i => {
      const start = -90 + i * 120 + gapDeg / 2;
      const end = start + segDeg;
      const [x1o, y1o] = pt(rOuter, start);
      const [x2o, y2o] = pt(rOuter, end);
      const [x2i, y2i] = pt(rInner, end);
      const [x1i, y1i] = pt(rInner, start);
      const lit = i < progress.segments;
      const d = `M ${x1i.toFixed(2)} ${y1i.toFixed(2)} L ${x1o.toFixed(2)} ${y1o.toFixed(2)} A ${rOuter} ${rOuter} 0 0 1 ${x2o.toFixed(2)} ${y2o.toFixed(2)} L ${x2i.toFixed(2)} ${y2i.toFixed(2)} A ${rInner} ${rInner} 0 0 0 ${x1i.toFixed(2)} ${y1i.toFixed(2)} Z`;
      return `<path class="caste-seg${lit ? ' caste-seg-lit' : ''}" d="${d}"></path>`;
    }).join('');
    const pulseClass = progress.segments >= 2 ? ' caste-ring-pulse' : '';
    const hyperClass = progress.kind === 'drilling' ? ' fusion-ring-hyper' : '';
    return `<svg class="caste-progress-ring${pulseClass}${hyperClass}" viewBox="0 0 34 34" aria-hidden="true">${wedges}</svg>`;
  }

  // Duplicate-in-hand warning glow: completely removed for both Sul and Human.
  // The pie-slice wedges in the cost disc are the sole indicator of fusion progress.
  _getDuplicateGlowClass(card, casteProgress) {
    return null;
  }

  _getCardTitleStyle(name) {
    if (!name) return '';
    const len = name.length;
    if (len > 22) return 'style="font-size: 6.2px; letter-spacing: 0px;"';
    if (len > 18) return 'style="font-size: 6.9px; letter-spacing: 0px;"';
    if (len > 14) return 'style="font-size: 7.8px; letter-spacing: 0.1px;"';
    // Everything at or under 14 characters shares this same capped size -
    // a short name must never fall through to an unbounded font-size from
    // elsewhere in the cascade (deckbuilder.css's .card-item .card-name
    // once leaked a much larger, fixed size onto this very class and
    // truncated short titles like "Tesla Walker"/"Devil" with an ellipsis).
    return 'style="font-size: 8.7px; letter-spacing: 0.2px;"';
  }

  _getCardDescStyle(descLen, loreLen, total) {
    if (total > 260) return 'font-size: 9.0px; line-height: 1.18;';
    if (total > 195) return 'font-size: 9.8px; line-height: 1.20;';
    if (total > 140) {
      if (descLen <= 60) return 'font-size: 11.2px; line-height: 1.24;';
      return 'font-size: 10.8px; line-height: 1.22;';
    }
    if (total > 95) return 'font-size: 11.8px; line-height: 1.25;';
    return 'font-size: 12.6px; line-height: 1.26;';
  }

  _getCardLoreStyle(descLen, loreLen, total) {
    if (total > 260) return 'font-size: 8.2px; line-height: 1.18;';
    if (total > 195) return 'font-size: 9.0px; line-height: 1.19;';
    if (total > 140) {
      if (loreLen > 100) return 'font-size: 9.6px; line-height: 1.22;';
      return 'font-size: 10.2px; line-height: 1.22;';
    }
    if (total > 95) return 'font-size: 10.8px; line-height: 1.23;';
    return 'font-size: 11.4px; line-height: 1.24;';
  }

  // Builds the same card markup for both the hand dock (buildHandDOM) and
  // the drag-lift preview (beginCardDrag) - one source of truth for what a
  // filled card looks like, so the preview always matches the real thing.
  buildCardInnerHTML(card, slotIndex, cost) {
    const isGold = inventory?.isSpecialCardActive ? inventory.isSpecialCardActive(card.name, 'gold') : (inventory?.hasSpecialCard ? inventory.hasSpecialCard(card.name, 'gold') : false);
    const goldImage = isGold ? getGoldCardImage(card.name) : null;
    const defaultImg = card.images && card.images.length > 0 ? card.images[0] : 'Marine_1.png';
    const imgPath = getCardThumbUrl(goldImage || defaultImg);
    const fallbackSrc = imgPath;
    const rawCard = (typeof getCardByName === 'function' ? getCardByName(card.name) : null) || card;
    const titleStyle = this._getCardTitleStyle ? this._getCardTitleStyle(card.name) : HUD.prototype._getCardTitleStyle(card.name);
    const desc = card.description ?? rawCard?.description ?? '';
    const lore = card.lore ?? rawCard?.lore ?? '';
    const descLen = (desc || '').length;
    const loreLen = (lore || '').length;
    const total = descLen + loreLen;
    const getDescStyle = this._getCardDescStyle || HUD.prototype._getCardDescStyle;
    const getLoreStyle = this._getCardLoreStyle || HUD.prototype._getCardLoreStyle;
    const descStyle = getDescStyle ? getDescStyle.call(this, descLen, loreLen, total) : '';
    const loreStyle = getLoreStyle ? getLoreStyle.call(this, descLen, loreLen, total) : '';

    const descHtml = desc ? `<p class="card-desc" style="${descStyle}; font-family: 'Rajdhani', sans-serif; font-weight: 600; color: #f1f5f9; text-shadow: 0 1px 2px #000, 0 0 5px rgba(0,0,0,0.98); -webkit-line-clamp: 14;">${desc}</p>` : '';
    const loreHtml = lore ? `<p class="card-lore" style="${loreStyle}; font-family: 'Rajdhani', sans-serif; font-weight: 500; font-style: italic; color: #cbd5e1; text-shadow: 0 1px 2px #000, 0 0 5px rgba(0,0,0,0.98); -webkit-line-clamp: 6; ${desc ? 'margin-top: 5px; padding-top: 4px; border-top: 1px solid rgba(203, 213, 225, 0.28);' : ''}">${lore}</p>` : '';
    const goldSheen = isGold ? '<div class="card-gold-holo-sheen"></div>' : '';
    const casteProgress = this._getCasteProgress(card);
    const casteRingHtml = this._buildCasteRingHtml(casteProgress);
    const duplicateGlowClass = this._getDuplicateGlowClass(card, casteProgress);
    const casteDuplicateGlowHtml = duplicateGlowClass ? `<div class="${duplicateGlowClass}"></div>` : '';

    const themeId = inventory?.getEquippedCardTheme ? inventory.getEquippedCardTheme() : 'glass';
    const isGlass = (themeId === 'glass' || themeId === 'fluted' || themeId === 'holo');
    const glassLayersHtml = isGlass ? `
      <div class="glass-button-dome" aria-hidden="true"></div>
      <div class="glass-bezel-fiberglow" aria-hidden="true"></div>
      <div class="glass-recess-glow" aria-hidden="true"></div>
      <div class="glass-overlay-container" aria-hidden="true">
        <div class="glass-material-tint"></div>
        <div class="glass-sheen-diagonal"></div>
        <div class="glass-glare-interactive"></div>
        <div class="glass-prismatic-border"></div>
        <div class="glass-noise-grain"></div>
      </div>
    ` : '';

    let statsHtml = '';
    if (card.hp || card.damage) {
      const hpVal = card.hp ? `<span class="card-footer-stat hp">${getIconSvg('hp', 12)} ${card.hp}</span>` : '';
      let dmgVal = '';
      if (card.damage) {
        const attacks = card.attackCount || 1;
        const totalDamage = card.damage * attacks;
        dmgVal = card.isKamikaze
          ? `<span class="card-footer-stat dmg kamikaze">${getIconSvg('kamikaze', 12)} ${totalDamage}</span>`
          : `<span class="card-footer-stat dmg">${getIconSvg('attack', 12)} ${totalDamage}</span>`;
      }
      statsHtml = `<div class="card-footer-stats">${hpVal}${dmgVal}</div>`;
    }

    return `
      ${glassLayersHtml}
      <div class="card-inner ${isGold ? 'card-gold-edition' : ''}">
        ${goldSheen}
        <img src="${imgPath}" alt="${card.name}" class="card-bg-img" draggable="false" onerror="if (!this.dataset.fallback) { this.dataset.fallback = '1'; this.src = '${fallbackSrc}'; }" />
        <div class="card-content-overlay">
          <div class="card-header">
            <span class="card-name" ${titleStyle} title="${card.name}">${card.name}</span>
            <div class="card-header-right">
              <span class="card-cost${cost > 0 ? '' : ' card-cost-free'}">${casteRingHtml}${cost > 0 ? cost : 'FREE'}</span>
            </div>
          </div>
          <div class="card-spacer"></div>
          <div class="card-body" style="padding: 16px 8px 9px 8px; background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(4, 7, 12, 0.72) 20%, rgba(2, 4, 8, 0.96) 100%);">
            ${descHtml}
            ${loreHtml}
            ${statsHtml}
          </div>
        </div>
        ${casteDuplicateGlowHtml}
      </div>
    `;
  }

  // Hyper Synthesis / Symbiosis text box: removed per design request.
  // Fusion progress is solely indicated by the cost disc pie-slice wedges.
  _buildFusionInfoHtml(card) {
    return '';
  }

  // How big a blown-up card may be right now. The card keeps its authored
  // proportions and is scaled as a whole - artwork, frame, cost and rules
  // text all grow by the same factor - so the zoom can never disagree with
  // the real card about what it looks like. Never below 1: the design box
  // itself is the floor, even on a viewport too small to grow past it.
  getCardZoomScale({ hover = false } = {}) {
    const vw = window.innerWidth || CARD_DESIGN_W;
    const vh = window.innerHeight || CARD_DESIGN_H;
    const fitted = Math.min(
      CARD_ZOOM_MAX_SCALE,
      (vh * CARD_ZOOM_VH_SHARE) / CARD_DESIGN_H,
      (vw * CARD_ZOOM_VW_SHARE) / CARD_DESIGN_W
    );
    return Math.max(1, fitted * (hover ? CARD_ZOOM_HOVER_SCALE_FACTOR : 1));
  }

  // Places a scale(k) copy of a card so it is horizontally centred on
  // centerX with its bottom edge at bottomY, clamped to stay fully on
  // screen - the outermost hand slots and a finger near the screen edge
  // would otherwise push half the card off it.
  //
  // Both previews are positioned by their CENTRE (left/top hold the centre
  // point, translate(-50%, -50%) does the rest), which is the one anchor
  // that survives scale() unmoved - see #card-drag-preview's
  // transform-origin note in css/main.css.
  // How much the hand is currently scaling its cards (1 on desktop, ~0.43
  // on a portrait phone). Measured off .card-scale's painted box rather
  // than read from --hand-card-scale so it is right in every breakpoint,
  // including the landscape block's fixed factor. Callers need it to
  // convert a distance they measured on SCREEN into the design-pixel
  // space the card's own transforms live in - see the melt/Reconsider
  // travel distances below, and docs/MOBILE_ANIMATION_PLAN.md §3.
  _handCardScale(wrapperEl) {
    const scaleEl = wrapperEl?.querySelector('.card-scale');
    const painted = scaleEl?.getBoundingClientRect().width || 0;
    return painted > 0 ? painted / CARD_DESIGN_W : 1;
  }

  // ---- The stage -------------------------------------------------------
  // A hand card animates inside .card-wrapper's overflow:hidden box, which
  // on a phone is exactly the size of the card - a lift, a convergence
  // flight or a 240px flare is cut away at the card's own edge, and
  // #cards-hand-container clips the row on top of that. So animations that
  // need more room than a hand slot don't play there: a copy is lifted onto
  // #card-stage, performs in screen coordinates with nothing clipping it,
  // and is dropped again. Same trick _placeCardPreview() above already uses
  // for drag and hover, and the same three-layer split as the hand
  // (docs/INVARIANTS.md #8): the stage item carries position and size, the
  // .card-slot inside it carries the animation in design pixels.
  //
  // The copy is placed and scaled exactly over the card it stands in for,
  // and that card is hidden while it performs, so the swap is invisible.
  // Used on desktop too, deliberately: the geometry is identical either way
  // (nothing clips there), and one path that always runs beats a mobile
  // special case nobody exercises. See docs/MOBILE_ANIMATION_PLAN.md §4.
  _stageCard(wrapperEl) {
    const stage = this.elCardStage;
    const scaleEl = wrapperEl?.querySelector('.card-scale');
    const slotEl = wrapperEl?.querySelector('.card-slot');
    if (!stage || !scaleEl || !slotEl) return null;

    const rect = scaleEl.getBoundingClientRect();
    if (rect.width <= 0) return null;
    const k = rect.width / CARD_DESIGN_W;

    const item = document.createElement('div');
    item.className = 'card-stage-item';
    item._cardRect = rect;
    item.style.left = `${rect.left.toFixed(1)}px`;
    item.style.top = `${rect.top.toFixed(1)}px`;
    item.style.transform = `scale(${k.toFixed(4)})`;

    const clone = slotEl.cloneNode(true);
    // The hand's own melt classes must not ride along on the copy - the
    // stage decides what this card is doing now.
    clone.classList.remove('card-melting-candidate', 'card-melting-source', 'card-melting-target');
    item.appendChild(clone);
    stage.appendChild(item);
    // Hidden here and now, not on updateHandUI()'s next pass: a frame with
    // both the card and its copy on screen is a visible double image.
    // _stageHiddenSlots is what KEEPS it hidden afterwards, across the hand
    // rebuilds that happen while the stage is busy.
    wrapperEl.classList.add('stage-source-hidden');
    return { item, slot: clone, k, rect };
  }

  // A staged position without a card in it: for effects that need the
  // stage's room but stand on their own (the Reconsider supernova, which
  // bursts where a card just was). Hides nothing, since nothing is being
  // stood in for.
  _stageSpot(wrapperEl) {
    const stage = this.elCardStage;
    const scaleEl = wrapperEl?.querySelector('.card-scale');
    if (!stage || !scaleEl) return null;
    const rect = scaleEl.getBoundingClientRect();
    if (rect.width <= 0) return null;

    const item = document.createElement('div');
    item.className = 'card-stage-item';
    item.style.left = `${rect.left.toFixed(1)}px`;
    item.style.top = `${rect.top.toFixed(1)}px`;
    item.style.transform = `scale(${(rect.width / CARD_DESIGN_W).toFixed(4)})`;
    stage.appendChild(item);
    return item;
  }

  // Keeps the staged convergence synchronized with the game loop:
  // Arms the safety watchdog and pauses/resumes animations if the game is paused.
  // The motion itself runs on the GPU compositor for 120 FPS buttery smoothness.
  _scrubMeltConvergence(remainingTime) {
    this._armMeltWatchdog();
    if (!this.elCardStage) return;
    const isPaused = this.game.isPaused === true;
    if (this._lastScrubPaused !== isPaused) {
      this._lastScrubPaused = isPaused;
      this.elCardStage.querySelectorAll('.card-slot').forEach(slot => {
        slot.style.animationPlayState = isPaused ? 'paused' : 'running';
      });
    }
  }

  // Recovery net for the convergence above: it is deliberately scrubbed off
  // the simulation's own clock rather than the wall clock (see
  // _scrubMeltConvergence()), so nothing else notices if requestAnimationFrame
  // itself stalls mid-flight - a background app-switch gesture, a long
  // touch-drag hogging the main thread, low-power throttling, all more
  // common on a phone mid-game than on a desktop. Re-armed every real frame
  // (_scrubMeltConvergence() above), so as long as frames keep coming this
  // never fires; if they stop, it still does, because setTimeout runs off
  // its own clock rather than rAF's. Clears the copies off the stage so the
  // real cards reappear instead of sitting frozen - the very next real frame
  // re-evaluates the pending melt fresh (updateHandUI()) and either restages
  // the convergence or plays the fusion, whichever the simulation has
  // actually reached by then.
  _armMeltWatchdog() {
    clearTimeout(this._meltWatchdogTimer);
    this._meltWatchdogTimer = setTimeout(() => {
      this._meltWatchdogTimer = null;
      if (this._stageOwner !== 'melt-convergence') return;
      this._clearStage();
      this._meltStageKey = null;
    }, MELT_WATCHDOG_MS);
  }

  _armConcentrationWatchdog() {
    clearTimeout(this._concentrationWatchdogTimer);
    this._concentrationWatchdogTimer = setTimeout(() => {
      this._concentrationWatchdogTimer = null;
      if (this._stageOwner !== 'concentration-implosion') return;
      this._concentrationSuctionActive = false;
      this._concentrationSuctionStart = 0;
      this._clearStage();
    }, 1200);
  }

  // Mounts a caption directly on the stage rather than inside a staged
  // card, centred above where that card sits. Everything inside a stage
  // item inherits the card's scale, which is right for the card's own
  // effects and wrong for a piece of text: on a phone the "HYPER UPGRADE"
  // badge came out at about 6px, inside the flare, i.e. invisible. A
  // caption belongs to the moment, not to the card, so it renders at the
  // size it was authored at on every device.
  _stageCaption(el, rect) {
    if (!this.elCardStage || !rect) return null;
    el.style.top = `${rect.top.toFixed(1)}px`;
    el.style.left = `${(rect.left + rect.width / 2).toFixed(1)}px`;
    this.elCardStage.appendChild(el);

    // Kept on screen, same margin the zoom preview uses: the caption is
    // centred on its card (the CSS centres it with translateX(-50%)) and is
    // wider than one, so a melt in the outermost hand slot would otherwise
    // run off the edge - and on a phone it is wider than half the viewport.
    //
    // Measured next frame rather than right here: el.offsetWidth forces a
    // synchronous layout flush, and this runs in the same tick as
    // playMeltAnimation()'s own card clone, class restarts and burst
    // overlay - one more forced flush stacked on top of those is what
    // turned this handoff into a visible stutter on a phone (see there).
    // The centred position set above is already correct except at the
    // hand's outer edges, so a one-frame-late correction is never seen.
    requestAnimationFrame(() => {
      if (!el.isConnected) return;
      const width = el.offsetWidth;
      if (width <= 0) return;
      const m = CARD_ZOOM_VIEWPORT_MARGIN_PX;
      const half = width / 2;
      const min = half + m;
      const max = Math.max(min, (window.innerWidth || width) - half - m);
      const centre = Math.min(max, Math.max(min, rect.left + rect.width / 2));
      el.style.left = `${centre.toFixed(1)}px`;
    });
    return el;
  }

  // Takes everything off the stage and lets the real cards show again. The
  // hidden-slot bookkeeping is re-applied every frame by updateHandUI(), so
  // clearing the set here is enough to reveal them even across a hand
  // rebuild that happened while the stage was busy.
  _clearStage() {
    clearTimeout(this._stageTimer);
    this._stageTimer = null;
    clearTimeout(this._meltWatchdogTimer);
    this._meltWatchdogTimer = null;
    clearTimeout(this._concentrationWatchdogTimer);
    this._concentrationWatchdogTimer = null;
    if (this.elCardStage) this.elCardStage.innerHTML = '';
    this._stageHiddenSlots = null;
    this._stageOwner = null;
    this._meltStageAnims = null;
    // Reveal right here rather than leaving it to updateHandUI()'s next
    // pass: the copies are already gone, and a card that waits a frame to
    // come back waits as long as the game loop does - which is forever
    // while the game is paused.
    this.elHandContainer?.querySelectorAll('.card-wrapper.stage-source-hidden')
      .forEach(w => w.classList.remove('stage-source-hidden'));
  }

  /**
   * Clears all animation, staging, and hand tracking state carried over
   * from a previous match. Called at every match boundary (startMatch,
   * campaign, exhibition, online) so the HUD starts clean.
   */
  resetForNewMatch() {
    this._hasRenderedPausedHud = false;
    this.hideTeammateDock();
    this._hideLowGraphicsHint?.();
    this._hideAiDifficultyHint?.();
    if (this._westGhostTimeout) { clearTimeout(this._westGhostTimeout); this._westGhostTimeout = null; }
    if (this._eastGhostTimeout) { clearTimeout(this._eastGhostTimeout); this._eastGhostTimeout = null; }
    this._lastWestHp = undefined;
    this._lastEastHp = undefined;
    this.elWesternDecreePill?.classList.add('hidden');
    this.elEasternDecreePill?.classList.add('hidden');
    this.elWesternDecreeBalloon?.classList.add('hidden');
    this.elEasternDecreeBalloon?.classList.add('hidden');
    if (this.elWesternShoutIntro) this.elWesternShoutIntro.textContent = '';
    if (this.elWesternShoutPunchline) {
      this.elWesternShoutPunchline.textContent = '';
      this.elWesternShoutPunchline.classList.remove('stamped');
    }
    if (this.elEasternShoutIntro) this.elEasternShoutIntro.textContent = '';
    if (this.elEasternShoutPunchline) {
      this.elEasternShoutPunchline.textContent = '';
      this.elEasternShoutPunchline.classList.remove('stamped');
    }

    // 1. Animation stage (melt convergence, concentration implosion)
    this._clearStage();
    this._meltStageKey = null;
    this._meltStageTotal = 0;

    // 2. Hand change tracking
    this._handDirty = true;
    this.lastHandSignature = '';
    const handLen = this._lastHandCardIds?.length || BALANCE?.maxHandSize || 5;
    this._lastHandCardIds = new Array(handLen).fill(null);
    this._lastHandCardInstances = new Array(handLen).fill(null);
    this._prevHand = null;

    // 3. Per-slot DOM cache — force buildHandDOM() to construct fresh elements
    this._cachedHandSlots = [];
    if (this.elHandContainer) this.elHandContainer.innerHTML = '';

    // 4. Event sequence trackers
    this.lastSeenConcentrationSeq = 0;
    this.lastSeenMentalExtractionSeq = 0;
    this.lastSeenMeltSeq = 0;
    this.lastSeenPlayErrorSeq = 0;
    this.lastSeenDrawErrorSeq = 0;

    // 5. Concentration suction hold
    this._concentrationSuctionActive = false;
    this._concentrationSuctionStart = 0;

    // 6. Deploy window / click suppression & reserved resources
    if (this._deployWindowSlots) this._deployWindowSlots.clear();
    if (this._suppressClickSlots) this._suppressClickSlots.clear();
    this._optimisticReservedPlasma = 0;

    // 7. Card zoom / drag
    this.endCardHoverSession?.();
    this._cardDrag = null;
    this._lastTouchTime = 0;
    this._touchActive = false;

    // 8. Force layout calculation on sidebar controls to eliminate WebKit cold-start clipping
    const sidebar = document.getElementById('sidebar-controls');
    if (sidebar) void sidebar.offsetHeight;

    // 9. Reset hacker intel DOM signature so new match immediately renders fresh hand
    if (this._hackerIntelClusters) {
      for (const t of ['western', 'eastern']) {
        if (this._hackerIntelClusters[t]) {
          this._hackerIntelClusters[t]._lastHandHtml = '';
        }
      }
    }
  }

  // Phase 1 of a melt: the two outer cards fly smoothly into the middle one.
  // Runs on the stage because the flight leaves the source card's own box on its
  // first frame - in the hand it was clipped to a sliver.
  _stageMeltConvergence(slots, resultSlot, totalTime, pendingMelt = null) {
    if (!this.elCardStage || !this.elHandContainer) return;
    this._clearStage();

    const targetRect = this.elHandContainer.children[resultSlot]
      ?.querySelector('.card-scale')?.getBoundingClientRect();
    if (!targetRect) return;

    const hidden = new Set();
    slots.forEach((slotIndex) => {
      const wrapper = this.elHandContainer.children[slotIndex];
      const staged = this._stageCard(wrapper);
      if (!staged) return;
      hidden.add(slotIndex);

      const speed = this.game.speedMultiplier || 1;
      const animDuration = Math.max(0.1, totalTime / speed);
      staged.slot.style.animationDuration = `${animDuration.toFixed(2)}s`;

      if (pendingMelt?.isPulp && pendingMelt.pulpSlots?.includes(slotIndex)) {
        staged.slot.classList.add('card-pulp-mimicry');
      }

      if (slotIndex === resultSlot) {
        staged.slot.classList.add('card-melting-target');
        return;
      }
      const from = wrapper.querySelector('.card-scale').getBoundingClientRect();
      // Measured on screen, applied inside the copy's own scale layer - so
      // it has to be expressed in design pixels (see _handCardScale()).
      const travel = (targetRect.left - from.left) / staged.k;
      const rot = travel < 0 ? -12 : 12;
      staged.slot.style.setProperty('--melt-target-x', `${travel.toFixed(1)}px`);
      staged.slot.style.setProperty('--melt-rot', `${rot}deg`);
      staged.slot.classList.add('card-melting-source');
    });

    if (hidden.size === 0) return;
    this._stageHiddenSlots = hidden;
    this._stageOwner = 'melt-convergence';
    this._meltStageTotal = totalTime > 0 ? totalTime : 1;
    this._armMeltWatchdog();
    // stage while the melt was still pending, or leave them lying there
    // after it resolved. The two real endings are both driven by the
    // simulation instead: the triplet fuses (playMeltAnimation() takes the
    // stage over) or it fizzles because one of its cards got played (the
    // pendingMelt key clears, and updateHandUI() clears the stage with it).
    //
    // _armMeltWatchdog() below is not that timer - it is a recovery net for
    // when neither ending arrives because requestAnimationFrame itself
    // stalled, and re-arms every real frame precisely so it stays inert
    // otherwise (see its own comment).
    this._armMeltWatchdog();
  }

  _placeCardPreview(el, k, centerX, bottomY) {
    const w = CARD_DESIGN_W * k;
    const h = CARD_DESIGN_H * k;
    const m = CARD_ZOOM_VIEWPORT_MARGIN_PX;
    const maxLeft = Math.max(m, (window.innerWidth || w) - w - m);
    const maxTop = Math.max(m, (window.innerHeight || h) - h - m);
    const left = Math.max(m, Math.min(maxLeft, centerX - w / 2));
    const top = Math.max(m, Math.min(maxTop, bottomY - h));
    el.style.left = `${(left + w / 2).toFixed(1)}px`;
    el.style.top = `${(top + h / 2).toFixed(1)}px`;
    el.style.transform = `translate(-50%, -50%) scale(${k.toFixed(4)})`;
  }

  // True when the primary pointing device has hover capability and fine accuracy (desktop mouse/trackpad).
  // Touch devices (iPad, iPhone, Android) report (hover: none) or (pointer: coarse) unless an external mouse is paired.
  _canHover() {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  }

  // True while a touch is actively touching the display, or within 1.5s after touch release
  // (the window in which iOS Safari emits synthetic mouseenter/pointerenter with pointerType: 'mouse').
  _isTouchInteraction() {
    if (this._touchActive) return true;
    if (this._lastTouchTime && (performance.now() - this._lastTouchTime < 1500)) return true;
    return false;
  }

  // Desktop hover-to-inspect, gated on a dwell the player pays once per
  // visit to the hand rather than once per card: the clock starts when the
  // pointer first reaches a card and keeps running as it slides along the
  // row, so the second card and every card after it opens the moment the
  // pointer arrives. Only leaving the hand row entirely resets it (see the
  // container's pointerleave in initElements).
  _scheduleCardZoom(slotIndex, card, slotDiv) {
    if (!this._canHover() || this._isTouchInteraction()) return;
    if (this._handHoverStart === null) this._handHoverStart = performance.now();
    const waited = performance.now() - this._handHoverStart;
    clearTimeout(this._cardZoomTimer);
    if (waited >= CARD_ZOOM_HOVER_DELAY_MS) {
      this._showCardZoomIfStillValid(slotIndex, card, slotDiv);
      return;
    }
    this._cardZoomTimer = setTimeout(
      () => this._showCardZoomIfStillValid(slotIndex, card, slotDiv),
      CARD_ZOOM_HOVER_DELAY_MS - waited
    );
  }

  _showCardZoomIfStillValid(slotIndex, card, slotDiv) {
    if (this._handHoverStart === null) return;
    if (!this._canHover() || this._isTouchInteraction()) return;
    // The hand rebuilds on every draw/play/melt, so the element this was
    // scheduled for can be gone (or mid-play, or already lifted into a
    // drag) by the time the dwell is up.
    if (!slotDiv.isConnected || this._cardDrag) return;
    if (slotDiv.closest('.card-wrapper')?.classList.contains('drag-source-hidden')) return;
    if (slotDiv.classList.contains('card-deploying')) return;
    this.showCardZoom(slotIndex, card, slotDiv);
  }

  showCardZoom(slotIndex, card, anchorEl) {
    const el = this.elCardZoom;
    if (!el || !card || card.pending || !anchorEl) return;

    // Live, not the cost captured when this hand was last built - the same
    // staleness the DRAW button's click handler guards against, since
    // buildHandDOM() only reruns when the hand's contents change, not when
    // a cost modifier does.
    const cost = this.game.cardSystem.getEffectiveCost(this.game.myTeam, card);
    const rarityClass = `rarity-${card.rarity.toLowerCase()}`;
    const raceClass = `race-${(card.race || 'terran').toLowerCase()}`;
    const meltClass = card.type === 'Melt' ? 'card-type-melt' : '';
    const casteClass = this._getCasteClass(card);
    const themeClass = this._getCardThemeClasses();
    el.className = `card-slot filled card-zoom-view ${rarityClass} ${raceClass} ${meltClass} ${casteClass} ${themeClass} affordable zoom-visible`;
    el.innerHTML = this.buildCardInnerHTML(card, slotIndex, cost);

    const rect = anchorEl.getBoundingClientRect();
    this._placeCardPreview(
      el,
      this.getCardZoomScale({ hover: true }),
      rect.left + rect.width / 2,
      rect.top - CARD_ZOOM_ANCHOR_GAP_PX
    );
    this._cardZoomSlot = slotIndex;
  }

  // Takes the zoom off screen but leaves the dwell clock running - the
  // pointer crossing from one card to the next, or a hand rebuild under a
  // resting pointer, is not the player leaving the hand.
  hideCardZoom() {
    clearTimeout(this._cardZoomTimer);
    this._cardZoomTimer = null;
    this._cardZoomSlot = null;
    const el = this.elCardZoom;
    if (!el) return;
    el.classList.remove('zoom-visible');
    // Dropping the markup as well keeps a stale card from flashing for one
    // frame when the next hover reuses this element for a different card.
    el.innerHTML = '';
  }

  // ...and this ends the visit itself, so the next hover pays the full
  // dwell again. Anything that means "the player is done looking at the
  // hand for now" belongs here rather than in hideCardZoom().
  endCardHoverSession() {
    this._handHoverStart = null;
    this.hideCardZoom();
  }

  // Lifts a full-size, always-readable copy of the held card into
  // #card-drag-preview, growing from the real card's current on-screen
  // spot/scale so it reads as picking the card up, not a sudden swap.
  // See buildHandDOM()'s pointer wiring for when this fires (armed after a
  // short hold or a small initial move - either way, a plain tap never
  // reaches this at all and keeps playing instantly).
  //
  // Takes the whole drag record because the lift now depends on HOW the
  // card is being held: touch has no hover, so a held card doubles as the
  // inspect view (Hearthstone's press-and-hold) - it lifts at the full
  // zoom scale and parks above the finger, where neither the finger nor
  // the hand dock covers the rules text. A mouse keeps the 1:1 lift it
  // always had, since hovering already gives it a bigger, calmer read.
  beginCardDrag(d) {
    if (!this.elDragPreview) return;
    const { slotIndex, card, cost, slotDiv: originSlotEl } = d;
    this.endCardHoverSession();
    this.game.audio?.playBark?.(card.race, 'select');

    // Invalidates any still-pending cleanup from a previous drag's
    // resolveCardDrag() (see there) - without this, quickly cancelling one
    // card and grabbing another (or the same one again) inside that
    // cleanup's 180ms window lets the STALE timeout fire after THIS drag
    // has already started, wiping the preview's content/opacity out from
    // under it mid-gesture.
    this._dragGeneration = (this._dragGeneration || 0) + 1;

    this._dragZoomed = d.pointerType !== 'mouse';
    // 0.85: touch preview at full fitted scale nearly filled the screen
    // (unlike desktop hover, which already backs off via
    // CARD_ZOOM_HOVER_SCALE_FACTOR) - see chat request "15% kleiner".
    this._dragScale = this._dragZoomed ? this.getCardZoomScale() * 0.85 : 1;

    const rect = originSlotEl.getBoundingClientRect();
    let startScale = 1;
    try {
      startScale = new DOMMatrix(getComputedStyle(originSlotEl).transform).a || 1;
    } catch { /* keep 1 (desktop, unscaled) */ }
    this._dragOriginCenter = { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };

    originSlotEl.closest('.card-wrapper')?.classList.add('drag-source-hidden');

    const preview = this.elDragPreview;
    this._renderDragPreviewCard(card, slotIndex, cost);

    preview.style.transition = 'none';
    preview.style.left = `${this._dragOriginCenter.x}px`;
    preview.style.top = `${this._dragOriginCenter.y}px`;
    preview.style.opacity = '1';
    preview.style.transform = `translate(-50%, -50%) scale(${startScale})`;
    // Force the browser to actually paint the line above before the next
    // one, so THIS change transitions instead of both getting coalesced
    // into a single, un-animated jump straight to the final state.
    void preview.offsetWidth;
    // left/top are transitioned too, not just transform: the lift no
    // longer ends where it started - it travels from the hand slot to its
    // spot at the pointer, which for touch is a whole card-height above
    // the finger. Without them that trip is an instant teleport.
    preview.style.transition = 'transform 0.14s ease-out, left 0.14s ease-out, top 0.14s ease-out';
    this._placeCardPreview(preview, this._dragScale,
      d.startX, this._dragPreviewBottomY(d.startY, this._dragScale));
  }

  // Paints one card into the drag preview. Its own method because the
  // lifted card is not fixed for the life of a drag: sliding a finger
  // along the hand swaps it (see _maybeSwitchDragCard).
  _renderDragPreviewCard(card, slotIndex, cost) {
    const rarityClass = `rarity-${card.rarity.toLowerCase()}`;
    const raceClass = `race-${(card.race || 'terran').toLowerCase()}`;
    const meltClass = card.type === 'Melt' ? 'card-type-melt' : '';
    const casteClass = this._getCasteClass(card);
    const themeClass = this._getCardThemeClasses();
    const preview = this.elDragPreview;
    preview.className = `card-slot filled ${rarityClass} ${raceClass} ${meltClass} ${casteClass} ${themeClass} affordable dragging${this._dragZoomed ? ' card-zoom-view' : ''}`;
    preview.innerHTML = this.buildCardInnerHTML(card, slotIndex, cost);
  }

  // Which hand slot the pointer is over, by hit-testing the wrappers' own
  // boxes rather than elementFromPoint: the card being dragged is hidden
  // (opacity 0, still hit-testable) and the preview floats above the row,
  // so "what is under the pointer" is a question about layout, not paint.
  _handSlotAtPoint(clientX, clientY) {
    const container = this.elHandContainer;
    if (!container) return null;
    const r = container.getBoundingClientRect();
    if (clientY < r.top || clientY > r.bottom || clientX < r.left || clientX > r.right) return null;
    const wrappers = container.children;
    for (let i = 0; i < wrappers.length; i++) {
      const wr = wrappers[i].getBoundingClientRect();
      if (clientX >= wr.left && clientX <= wr.right) return { index: i, wrapper: wrappers[i] };
    }
    return null;
  }

  // Touch's answer to hovering: with a card held, sliding sideways along
  // the hand hands the lift over to whichever card the finger is now on,
  // so a player can read the whole hand in one continuous press instead of
  // lifting and re-pressing per card.
  //
  // Only while the gesture still reads as "browsing": the finger has to be
  // inside the hand row, and the drag must not already be far enough up to
  // play on release - a card on its way to the battlefield never silently
  // becomes a different card. Returns true if the lift actually moved.
  _maybeSwitchDragCard(clientX, clientY, dy) {
    const d = this._cardDrag;
    if (!d || !this._dragZoomed) return false; // mouse keeps its 1:1 lift; hover covers reading there
    if (dy <= CARD_PLAY_THRESHOLD_PX) return false;

    const hit = this._handSlotAtPoint(clientX, clientY);
    if (!hit || hit.index === d.slotIndex) return false;

    // Empty (draw) slots and cards whose identity hasn't arrived yet are
    // not something to inspect - the finger passes over them without the
    // held card changing.
    const card = this.game.cardSystem.hands[this.game.myTeam]?.[hit.index];
    if (!card || card.pending) return false;
    const slotDiv = hit.wrapper.querySelector('.card-slot');
    if (!slotDiv) return false;

    const prevWrapper = d.slotDiv.closest('.card-wrapper');
    if (prevWrapper && !prevWrapper.classList.contains('slot-deploying')) {
      prevWrapper.classList.remove('drag-source-hidden');
    }
    hit.wrapper.classList.add('drag-source-hidden');

    d.slotIndex = hit.index;
    d.card = card;
    d.cost = this.game.cardSystem.getEffectiveCost(this.game.myTeam, card);
    d.slotDiv = slotDiv;
    // Re-anchor the gesture to where the finger is now, so the pull-up
    // needed to play THIS card is measured from the point it was picked
    // up, exactly as it would be had the press started here.
    d.startX = clientX;
    d.startY = clientY;
    d.lastMoveY = clientY;
    d.velocityY = 0;

    // Cancelling now has to shrink back into the card the finger ended on,
    // not the one the press happened to start on (resolveCardDrag reads
    // this for the put-back animation).
    const newRect = slotDiv.getBoundingClientRect();
    this._dragOriginCenter = {
      x: newRect.left + newRect.width / 2,
      y: newRect.top + newRect.height / 2
    };

    this._renderDragPreviewCard(card, hit.index, d.cost);
    return true;
  }

  // Where the lifted card's bottom edge sits relative to the pointer.
  // Mouse: unchanged from before the zoom existed - the cursor grabs the
  // card a quarter of the way up from its bottom edge. Touch: the entire
  // card sits clear above the finger, because on touch this preview IS the
  // readable view and a fingertip parked in the middle of it is not.
  _dragPreviewBottomY(clientY, k) {
    return this._dragZoomed
      ? clientY - CARD_ZOOM_POINTER_GAP_PX
      : clientY + 0.25 * CARD_DESIGN_H * k;
  }

  // clientX/clientY: current pointer position. dy: vertical travel in CSS
  // px since the press started (negative = up, toward the field).
  updateCardDrag(clientX, clientY, dy) {
    const preview = this.elDragPreview;
    if (!preview) return;
    const k = this._dragScale || 1;
    preview.style.transition = 'none';
    this._placeCardPreview(preview, k, clientX, this._dragPreviewBottomY(clientY, k));

    preview.classList.toggle('armed-play', dy <= CARD_PLAY_THRESHOLD_PX);
  }

  // outcome: 'play' | 'cancel'.
  resolveCardDrag(outcome, slotIndex) {
    this._lastTouchTime = performance.now();
    this.endCardHoverSession();
    const myGeneration = this._dragGeneration;

    if (outcome === 'play') {
      const originWrapper = this.elHandContainer?.children[slotIndex];
      if (originWrapper) {
        const s = originWrapper.querySelector('.card-slot');
        const playedCard = this.game.cardSystem.hands[this.game.myTeam][slotIndex];
        const cost = playedCard ? this.game.cardSystem.getEffectiveCost(this.game.myTeam, playedCard) : 0;
        this.game.audio?.playCardPlay?.();
        this._enterDeployWindow(slotIndex, playedCard, cost, s, originWrapper);
        // The concentration spell has its own suction performance on top
        // of the deploy window - both ran here before the window existed
        // and both still do, the window being the plain "something is
        // happening" layer under it.
        if (playedCard?.spellEffect === 'concentration' || playedCard?.spellEffect === 'mental_extraction') {
          this._startConcentrationVFX(slotIndex);
        }
      }
      this.game.enqueueCommand({ type: 'PLAY', team: this.game.myTeam, slot: slotIndex });
    }

    const preview = this.elDragPreview;
    if (!preview) return;
    preview.classList.remove('armed-play');
    preview.style.transition = 'transform 0.16s ease-in, left 0.16s ease-in, top 0.16s ease-in, opacity 0.16s ease-in';

    if (outcome === 'play') {
      preview.style.transform = `translate(-50%, -160%) scale(${((this._dragScale || 1) * 0.85).toFixed(4)})`;
      preview.style.opacity = '0';
    } else {
      // Cancelled - shrink back down onto the spot it lifted from, same
      // motion as beginCardDrag() in reverse, so it reads as "put back".
      const c = this._dragOriginCenter;
      if (c) {
        preview.style.left = `${c.x}px`;
        preview.style.top = `${c.y}px`;
      }
      preview.style.transform = 'translate(-50%, -50%) scale(0.3)';
      preview.style.opacity = '0';
    }

    setTimeout(() => {
      // A newer drag has since started (see beginCardDrag) - it owns the
      // preview element and .drag-source-hidden state now, so this stale
      // cleanup must not touch either.
      if (this._dragGeneration !== myGeneration) return;
      preview.classList.remove('dragging');
      preview.innerHTML = '';
      preview.style.opacity = '';
      document.querySelectorAll('.card-wrapper.drag-source-hidden')
        .forEach(el => {
          if (!el.classList.contains('slot-deploying')) {
            el.classList.remove('drag-source-hidden');
          }
        });
    }, 180);
  }

  // Deploy Window - "lieber entsteht ein Deploy-Fenster, als dass zwischen
  // Klick und Reaktion der UI Zeit vergeht". Online, a PLAY command only
  // reaches the simulation LOOKAHEAD_TURNS later (TurnScheduler: 6 turns =
  // 1200ms on the Strato HTTP path, see docs/INVARIANTS.md rule 5, which
  // is why that budget is not the thing to shorten). Without this the
  // click was followed by 1.2s of nothing whatsoever: the plasma readout
  // did not move and no unit appeared, which reads as a dropped input
  // rather than as latency. These two methods hold a LOCAL, purely
  // presentational "deploying..." state across that gap.
  //
  // Nothing here touches the simulation, so none of it can desync the
  // lockstep (CLAUDE.md's one rule): the reservation below is subtracted
  // from the DISPLAYED plasma only - economy.resources is still moved by
  // the PLAY command alone, on both machines, at the same tick.
  _enterDeployWindow(slotIndex, card, cost, slotDiv, wrapperDiv) {
    if (!card) return;
    if (!this._deployWindowSlots) this._deployWindowSlots = new Map();
    // A second gesture landing on a slot that is already deploying (a
    // double tap racing the same turn) must not reserve the cost twice -
    // the window already standing owns it, and the extra PLAY command
    // will simply bounce off an empty slot when it resolves.
    if (this._deployWindowSlots.has(slotIndex)) return;

    this._deployWindowSlots.set(slotIndex, {
      kind: 'play',
      card,
      // What this slot held when the click happened. Both window kinds end
      // on the same rule - "the slot's occupant changed" - which for a play
      // means the card left, and for a draw means a card arrived.
      slotIdAtClick: card.instanceId || card.id,
      cost: cost || 0,
      slotDiv,
      wrapperDiv,
      t: performance.now()
    });
    this._optimisticReservedPlasma = (this._optimisticReservedPlasma || 0) + (cost || 0);

    if (slotDiv) {
      // The immediate "card played" feedback, and deliberately the exact
      // same one a local game gives: .card-deploying flashes the card cyan
      // and warps it up out of the hand over 0.38s (see css/cards.css).
      // Online that used to be replaced by a dimmed, spinner-covered card
      // sitting in the slot - which reads as "this card is disabled and
      // pending", not "played", and is the delay the player was feeling
      // even though the click itself was handled instantly. The waiting
      // happens AFTER the card has visibly gone, in the emptied slot,
      // exactly where a local game would already show the next draw.
      slotDiv.classList.add('card-deploying');
      slotDiv.style.pointerEvents = 'none';
    }
    if (wrapperDiv) {
      wrapperDiv.classList.add('slot-deploying');
      // The drag path hides the slot a card was lifted out of (opacity:0,
      // see .card-wrapper.drag-source-hidden in css/main.css). Leaving
      // that on would make the deploy window an invisible hole for the
      // whole 1.2s - the exact "my click went nowhere" feeling this is
      // here to remove - so the slot comes back the moment it stops being
      // a drag source and starts being a deploying one.
      wrapperDiv.classList.remove('drag-source-hidden');
    }

    // Immediate confirmation on the battlefield too, at the spawn point
    // the unit will actually walk out of. Cosmetic and local: deployBeacons
    // is neither hashed nor serialised (see calcStateHash() and
    // createGameStateSnapshot() in js/net/stateSync.js).
    this.game.battlefield?.addDeployBeacon?.(this.game.myTeam, card);

    // Repaint the plasma readout against the reservation right now rather
    // than waiting for the next update(dt) frame - the whole point is that
    // the number moves in the same gesture as the click.
    this.updateHandUI();
  }

  // Draw Window - the same idea for the other half of the hand. Buying a
  // card is a DRAW command and crosses the same lockstep barrier a PLAY
  // does, so online it had the identical problem and none of the answer:
  // the slot kept saying "⚡ Draw Card" at full brightness, the plasma
  // readout did not move, and the button stayed live - so the obvious
  // reaction to a click that seemed to do nothing was to click again,
  // queueing a second DRAW and paying for it twice.
  //
  // Same contract as the deploy window: local, presentational, reserves
  // against the DISPLAYED plasma only, never touches the simulation.
  _enterDrawWindow(slotIndex, cost, slotDiv, wrapperDiv) {
    if (!this._deployWindowSlots) this._deployWindowSlots = new Map();
    if (this._deployWindowSlots.has(slotIndex)) return;

    this._deployWindowSlots.set(slotIndex, {
      kind: 'draw',
      card: null,
      slotIdAtClick: null, // an empty slot - it ends when a card lands here
      cost: cost || 0,
      slotDiv,
      wrapperDiv,
      t: performance.now()
    });
    this._optimisticReservedPlasma = (this._optimisticReservedPlasma || 0) + (cost || 0);

    this._applyDrawWindowVisual(slotDiv);
    if (this._cachedHandSlots) {
      delete this._cachedHandSlots[slotIndex];
    }
    this.updateHandUI();
  }

  // The empty slot's "buy" face turned into its "on its way" face. Applied
  // both on the click and again after a hand rebuild, so it survives a draw
  // resolving in some other slot.
  _applyDrawWindowVisual(slotDiv) {
    if (!slotDiv) return;
    slotDiv.classList.add('card-draw-window');
    slotDiv.style.pointerEvents = 'none';
    const icon = slotDiv.querySelector('.draw-icon');
    const label = slotDiv.querySelector('.draw-label');
    const cost = slotDiv.querySelector('.draw-cost');
    if (icon) {
      icon.innerHTML = getIconSvg('hourglass', 22);
    }
    if (label) label.textContent = 'Drawing...';
    // The price is settled the moment the click is accepted - leaving it on
    // screen next to "Drawing..." reads as "you still owe this".
    if (cost) cost.textContent = 'PAID';
  }

  _exitDeployWindow(slotIndex) {
    const entry = this._deployWindowSlots?.get(slotIndex);
    if (!entry) return;
    this._deployWindowSlots.delete(slotIndex);
    this._optimisticReservedPlasma =
      Math.max(0, (this._optimisticReservedPlasma || 0) - (entry.cost || 0));

    // buildHandDOM() may have replaced these nodes since the window opened
    // (it re-adopts a live window's fresh nodes into the same entry), so
    // this clears whichever pair the entry is currently holding.
    if (entry.slotDiv) {
      entry.slotDiv.classList.remove('card-deploying', 'card-deploy-window', 'card-draw-window');
      entry.slotDiv.style.pointerEvents = '';
    }
    entry.wrapperDiv?.classList.remove('slot-deploying');

    if (entry.kind === 'draw' && entry.slotDiv) {
      const icon = entry.slotDiv.querySelector('.draw-icon');
      const label = entry.slotDiv.querySelector('.draw-label');
      const cost = entry.slotDiv.querySelector('.draw-cost');
      if (icon) {
        icon.innerHTML = getIconSvg('energy', 22);
      }
      if (label) label.textContent = 'Draw Card';
      if (cost) {
        const isContracts = !!this.game?.battlefield?.moduleSystem?.hasCongressBuff(this.game?.myTeam, 'government_contracts');
        const drawCost = this.game?.cardSystem?.getEffectiveDrawCost ? this.game.cardSystem.getEffectiveDrawCost(this.game.myTeam) : (this.game?.cardSystem?.drawCost ?? 2);
        cost.innerHTML = isContracts ? 'FREE (0)' : (drawCost > 0 ? `${drawCost} ${getIconSvg('plasma', 12)}` : 'FREE');
      }
    }
    if (this._cachedHandSlots) {
      delete this._cachedHandSlots[slotIndex];
    }
  }

  // playSlot()/drawSlot() - the ONE place a PLAY or DRAW command actually
  // gets enqueued from player input. buildHandDOM()'s click listeners
  // below call these; so does the number-key/'D' hotkey handler in
  // js/main.js. Before this they were two separate implementations, and
  // the hotkey one skipped the affordability re-check, the audio and
  // _enterDeployWindow()/_enterDrawWindow() entirely - a card played with
  // '1'-'5' or drawn with 'D' enqueued the command just fine, but the
  // instant "played"/"drawing" feedback those two methods exist for
  // (see their own comments) never appeared, so keyboard play looked
  // exactly like the pre-Deploy-Window regression this whole mechanism
  // was built to fix. One shared implementation means a future change to
  // either can't drift between input methods the way that did.
  //
  // Both look their DOM nodes up fresh from the slot index rather than
  // requiring a caller-supplied slotDiv/wrapperDiv - a hotkey has no
  // element to hand in, only the index the number key names.
  // My team's scheduled triplet fusion, or null. Always null online: the
  // server melts on its own and the client never ticks CardSystem.update(),
  // so a pending melt seen here could only be a stale one that never
  // resolves and would lock its slots for the rest of the match.
  _getPendingMelt() {
    if (this.game.isOnline) return null;
    return this.game.cardSystem.pendingMelts[this.game.myTeam] || null;
  }

  playSlot(slotIndex) {
    if (this.game?.activeTargetingPower) {
      this.game.cancelTargeting();
    }

    // Disallow playing cards that are currently in a pending triplet fusion
    if (this._getPendingMelt()?.slots?.includes(slotIndex)) {
      return false;
    }

    const card = this.game.cardSystem.hands[this.game.myTeam]?.[slotIndex];
    if (!card || card.pending) return false;

    const wrapperDiv = this.elHandContainer?.children[slotIndex];
    const slotDiv = wrapperDiv?.querySelector('.card-slot');

    const livePlasma = this.game.economy.resources[this.game.myTeam].plasma
      - (this._optimisticReservedPlasma || 0);
    const liveCost = this.game.cardSystem.getEffectiveCost(this.game.myTeam, card);
    if (livePlasma < liveCost) {
      if (slotDiv) this.shakeInsufficientFunds(slotDiv);
      this.game.audio?.playError?.();
      return false;
    }

    this.game.audio?.playCardPlay?.();
    if (slotDiv && wrapperDiv) {
      this._enterDeployWindow(slotIndex, card, liveCost, slotDiv, wrapperDiv);
    }
    if (card.spellEffect === 'concentration' || card.spellEffect === 'mental_extraction') {
      this._startConcentrationVFX(slotIndex);
    }
    this.game.enqueueCommand({ type: 'PLAY', team: this.game.myTeam, slot: slotIndex });
    return true;
  }

  drawSlot(slotIndex) {
    if (this.game?.activeTargetingPower) {
      this.game.cancelTargeting();
    }

    if (this.game.cardSystem.hands[this.game.myTeam]?.[slotIndex]) return false; // slot isn't empty - nothing to draw into

    const wrapperDiv = this.elHandContainer?.children[slotIndex];
    const slotDiv = wrapperDiv?.querySelector('.card-slot');

    const livePlasma = this.game.economy.resources[this.game.myTeam].plasma
      - (this._optimisticReservedPlasma || 0);
    const liveDrawCost = this.game.cardSystem.getEffectiveDrawCost ? this.game.cardSystem.getEffectiveDrawCost(this.game.myTeam) : this.game.cardSystem.drawCost;
    if (livePlasma < liveDrawCost) {
      if (slotDiv) this.shakeInsufficientFunds(slotDiv);
      this.game.audio?.playError?.();
      return false;
    }

    if (slotDiv) {
      slotDiv.classList.add('draw-clicking');
      setTimeout(() => slotDiv.classList.remove('draw-clicking'), 150);
    }
    this.game.audio?.playCardDraw?.();
    if (slotDiv && wrapperDiv) {
      this._enterDrawWindow(slotIndex, liveDrawCost, slotDiv, wrapperDiv);
    }
    this.game.enqueueCommand({ type: 'DRAW', team: this.game.myTeam, slot: slotIndex });
    return true;
  }

  // Starts tracking a possible drag from a card's own pointerdown - see
  // buildHandDOM()'s wiring. Actually lifting the preview only happens
  // once armed (_armCardDrag), not here; this only records where things
  // started and arranges to notice enough movement or hold time to arm.
  _startCardPress(e, slotIndex, card, cost, slotDiv) {
    if (this.game?.activeTargetingPower) {
      this.game.cancelTargeting();
    }
    if (e.pointerType !== 'mouse') {
      this._lastTouchTime = performance.now();
      this._touchActive = true;
    }
    this.endCardHoverSession();
    if (this._cardDrag) return; // one drag at a time
    if (this.game.isOnline && (this.game.isOnlinePaused || this.game.onlinePauseState)) return; // match is paused
    if (this._getPendingMelt()?.slots?.includes(slotIndex)) return; // actively fusing
    // A fresh press is a fresh gesture: whatever the last one asked to
    // swallow has either already been swallowed or never arrived, and
    // keeping it armed would eat this press's own click instead.
    this._suppressClickSlots.clear();

    const d = {
      slotIndex, card, cost, slotDiv,
      // slotIndex moves when a held card is swapped mid-drag; this one
      // does not - it is where the synthetic click will be delivered.
      pressedSlotIndex: slotIndex,
      pointerId: e.pointerId,
      pointerType: e.pointerType,
      startX: e.clientX, startY: e.clientY,
      armed: false,
      lastMoveT: performance.now(), lastMoveY: e.clientY, velocityY: 0,
      armTimer: null, watchdogTimer: null
    };
    this._cardDrag = d;

    // Mouse doesn't have touch's tap-vs-hold ambiguity - a deliberate
    // click can easily take longer than this delay without the user
    // meaning to drag at all (mouse button physically held slightly
    // longer than a quick tap), so only touch/pen get armed by time
    // alone; mouse only arms on genuine movement, checked in
    // _onCardDragMove regardless of pointer type.
    if (e.pointerType !== 'mouse') {
      d.armTimer = setTimeout(() => this._armCardDrag(), CARD_DRAG_ARM_MS);
    }
    try { slotDiv.setPointerCapture(e.pointerId); } catch { /* best-effort */ }

    // Last-resort safety net - the window-level move/up/cancel listeners
    // above already cover every realistic release path, but "realistic"
    // isn't "guaranteed"; this keeps a truly orphaned drag from being
    // stuck forever instead of just uncomfortably long.
    d.watchdogTimer = setTimeout(() => {
      if (this._cardDrag !== d) return;
      this._onCardDragEnd({ pointerId: d.pointerId, clientY: d.startY }, true);
    }, 15000);
  }

  _armCardDrag() {
    const d = this._cardDrag;
    if (!d || d.armed) return;
    d.armed = true;
    clearTimeout(d.armTimer);
    this.beginCardDrag(d);
  }

  _onCardDragMove(e) {
    const d = this._cardDrag;
    if (!d || e.pointerId !== d.pointerId) return;
    let dy = e.clientY - d.startY;
    if (!d.armed) {
      if (Math.hypot(e.clientX - d.startX, dy) > CARD_DRAG_ARM_MOVE_PX) {
        this._armCardDrag();
      } else {
        return;
      }
    }
    // A swap re-anchors the gesture to the pointer, so the travel this
    // move reports has to be re-read from the new anchor - otherwise the
    // frame right after a swap would place the card (and judge the play
    // threshold) using the distance dragged since the PREVIOUS card.
    if (this._maybeSwitchDragCard(e.clientX, e.clientY, dy)) {
      dy = e.clientY - d.startY;
    }
    const now = performance.now();
    const dt = now - d.lastMoveT;
    if (dt > 4) {
      d.velocityY = (e.clientY - d.lastMoveY) / dt;
      d.lastMoveT = now;
      d.lastMoveY = e.clientY;
    }
    this.updateCardDrag(e.clientX, e.clientY, dy);
  }

  _onCardDragEnd(e, isCancel) {
    if (e?.pointerType !== 'mouse') {
      this._lastTouchTime = performance.now();
      this._touchActive = false;
    }
    this.endCardHoverSession();
    const d = this._cardDrag;
    if (!d || e.pointerId !== d.pointerId) return;
    clearTimeout(d.armTimer);
    clearTimeout(d.watchdogTimer);
    this._cardDrag = null;
    if (!d.armed) return; // untouched - the card's own 'click' listener plays it instead
    const dy = e.clientY - d.startY;
    this._suppressClickSlots = new Set([d.pressedSlotIndex, d.slotIndex]);
    if (isCancel) {
      this.resolveCardDrag('cancel', d.slotIndex);
      return;
    }
    if (dy <= CARD_PLAY_THRESHOLD_PX) {
      this.resolveCardDrag('play', d.slotIndex);
    } else {
      this.resolveCardDrag('cancel', d.slotIndex);
    }
  }

  // Cockpit Impact Shock & EMP Jolt (Zero-GC, 100% GPU Compositor)
  // Triggered on heavy explosions, superweapon detonations, or base hits >= 80 HP.
  triggerImpactJolt(intensity = 1) {
    if (this._joltTimeout) {
      clearTimeout(this._joltTimeout);
    }
    const topHud = this.elTopHud;
    const bottomHud = this.elBottomHud || (this.elBottomHud = document.getElementById('bottom-hud'));
    const scanlines = this.elScanlines || (this.elScanlines = document.getElementById('battlefield-scanlines'));

    if (topHud) {
      topHud.classList.remove('hud-impact-jolt');
      void topHud.offsetWidth; // Force CSS animation restart
      topHud.classList.add('hud-impact-jolt');
    }
    if (bottomHud) {
      bottomHud.classList.remove('hud-impact-jolt');
      void bottomHud.offsetWidth;
      bottomHud.classList.add('hud-impact-jolt');
    }

    // Heavy shockwave or superweapon: trigger CRT EMP scanline glitch
    if (intensity >= 18 && scanlines) {
      scanlines.classList.remove('scanlines-emp-glitch');
      void scanlines.offsetWidth;
      scanlines.classList.add('scanlines-emp-glitch');
    }

    this._joltTimeout = setTimeout(() => {
      topHud?.classList.remove('hud-impact-jolt');
      bottomHud?.classList.remove('hud-impact-jolt');
      scanlines?.classList.remove('scanlines-emp-glitch');
      this._joltTimeout = null;
    }, 240);
  }

  update(dt) {
    if (this.game.isPaused) {
      if (this._hasRenderedPausedHud) {
        return;
      }
      this._hasRenderedPausedHud = true;
    } else {
      this._hasRenderedPausedHud = false;
    }

    // 0. Update Animated 8-Bit Avatars
    if (this.avatarRenderer) {
      this.avatarRenderer.update(dt);
    }
    const res = this.game.economy.resources[this.game.myTeam];
    const baseW = this.game.battlefield.bases.western;
    const baseE = this.game.battlefield.bases.eastern;

    // 1. Update Base HP Bars
    // Current HP only - the bar itself shows the ratio, and the max is the
    // same 1500 in every match, so "/ 1500 HP" only ate label width (it
    // used to be spelled out on desktop and landscape).
    // 1. Update Base HP Bars (dirty-checked to eliminate redundant style/DOM thrashing)
    const wHpRound = Math.round(baseW.hp);
    if (this._lastWestHp !== wHpRound) {
      const isInitial = this._lastWestHp === undefined;
      const isDamage = !isInitial && wHpRound < this._lastWestHp;
      const dmg = isDamage ? (this._lastWestHp - wHpRound) : 0;
      this._lastWestHp = wHpRound;
      if (this.elWesternHp && this.elWesternHpText) {
        const ratio = Math.max(0, baseW.hp / baseW.maxHp);
        this.elWesternHp.style.width = `${ratio * 100}%`;
        this.elWesternHpText.innerText = `${wHpRound}`;
        this.elWesternHp.classList.toggle('critical-base-alarm', ratio > 0 && ratio <= 0.09);
        if (this.elWesternGhostHp) {
          if (isDamage) {
            if (this._westGhostTimeout) clearTimeout(this._westGhostTimeout);
            this._westGhostTimeout = setTimeout(() => {
              if (this.elWesternGhostHp) this.elWesternGhostHp.style.width = `${ratio * 100}%`;
            }, 220);
          } else {
            if (this._westGhostTimeout) clearTimeout(this._westGhostTimeout);
            this.elWesternGhostHp.style.width = `${ratio * 100}%`;
          }
        }
      }
      if (isDamage) {
        this.avatarRenderer?.triggerDistress?.('western', dmg);
        if (dmg >= 80) {
          this.triggerImpactJolt(dmg >= 150 ? 25 : 15);
        }
      }
    }
    const eHpRound = Math.round(baseE.hp);
    if (this._lastEastHp !== eHpRound) {
      const isInitial = this._lastEastHp === undefined;
      const isDamage = !isInitial && eHpRound < this._lastEastHp;
      const dmg = isDamage ? (this._lastEastHp - eHpRound) : 0;
      this._lastEastHp = eHpRound;
      if (this.elEasternHp && this.elEasternHpText) {
        const ratio = Math.max(0, baseE.hp / baseE.maxHp);
        this.elEasternHp.style.width = `${ratio * 100}%`;
        this.elEasternHpText.innerText = `${eHpRound}`;
        this.elEasternHp.classList.toggle('critical-base-alarm', ratio > 0 && ratio <= 0.09);
        if (this.elEasternGhostHp) {
          if (isDamage) {
            if (this._eastGhostTimeout) clearTimeout(this._eastGhostTimeout);
            this._eastGhostTimeout = setTimeout(() => {
              if (this.elEasternGhostHp) this.elEasternGhostHp.style.width = `${ratio * 100}%`;
            }, 220);
          } else {
            if (this._eastGhostTimeout) clearTimeout(this._eastGhostTimeout);
            this.elEasternGhostHp.style.width = `${ratio * 100}%`;
          }
        }
      }
      if (isDamage) {
        this.avatarRenderer?.triggerDistress?.('eastern', dmg);
      }
    }

    // 2. Update Resource Dashboard - plasma is the only resource.
    // Race-gated gauge theme (docs/RACE_PLASMA_GAUGE_PARADIGMA.md) - same
    // race-gate pattern as js/main.js's reticle-color/superweapon branches
    // (battlefield.bases[myTeam].race), never UI state.
    const myRace = this.game.battlefield?.bases?.[this.game.myTeam]?.race || 'Human';
    const isSulTheme = myRace === 'Sul';
    if (this._lastIsSulTheme !== isSulTheme) {
      this._lastIsSulTheme = isSulTheme;
      const themeVal = isSulTheme ? 'sul' : 'human';
      this.elPlasmaCapsule?.setAttribute('data-plasma-theme', themeVal);
      this.elTopHud?.setAttribute('data-plasma-theme', themeVal);
    }

    if (this.elPlasmaCur && this.elPlasmaCap) {
      const effectivePlasma = Math.max(0, res.plasma - (this._optimisticReservedPlasma || 0));
      const plasmaFloor = Math.floor(effectivePlasma);
      if (this._lastPlasmaFloor !== plasmaFloor || this._lastMaxPlasma !== res.maxPlasma) {
        this._lastPlasmaFloor = plasmaFloor;
        this._lastMaxPlasma = res.maxPlasma;
        this.elPlasmaCur.innerText = `${plasmaFloor}`;
        this.elPlasmaCap.innerText = ` / ${res.maxPlasma}`;
        if (this.elPlasmaCurSul) this.elPlasmaCurSul.innerText = `${plasmaFloor}`;
        if (this.elPlasmaCapSul) this.elPlasmaCapSul.innerText = ` / ${res.maxPlasma}`;
      }

      // Capped Plasma Overload State (100% Capacity Glow & Card Hand Surge)
      const isPlasmaCapped = effectivePlasma >= res.maxPlasma;
      if (this._lastPlasmaCapped !== isPlasmaCapped) {
        this._lastPlasmaCapped = isPlasmaCapped;
        this.elPlasmaCapsule?.classList.toggle('plasma-capped', isPlasmaCapped);
        this.elHandContainer?.classList.toggle('plasma-capped', isPlasmaCapped);
      }

      const pct = Math.max(0, Math.min(100, Math.round((effectivePlasma / res.maxPlasma) * 100)));
      if (this._lastPlasmaPct !== pct) {
        this._lastPlasmaPct = pct;
        const pctStr = `${pct}%`;
        if (this.elPlasma) {
          this.elPlasma.style.setProperty('--plasma-pct', pctStr);
        }
        if (this.elPlasmaCapsule) {
          this.elPlasmaCapsule.style.setProperty('--plasma-pct', pctStr);
        }
        if (this.elPlasmaFluid) {
          this.elPlasmaFluid.style.setProperty('--plasma-pct', pctStr);
        }

        // Dynamic gauge segments (seg-1 bottom to seg-8 top: fills from bottom to top)
        if (this.elPlasmaSegments && this.elPlasmaSegments.length > 0) {
          const totalSegs = this.elPlasmaSegments.length;
          const activeCount = Math.round((pct / 100) * totalSegs);
          if (this._lastActiveCount !== activeCount) {
            this._lastActiveCount = activeCount;
            for (let idx = 0; idx < totalSegs; idx++) {
              const seg = this.elPlasmaSegments[idx];
              // DOM order has seg-8 at index 0 (top) down to seg-1 at totalSegs-1 (bottom).
              // Fills from bottom to top: seg-1 (bottom) lights first, seg-8 (top) lights last.
              const segNum = totalSegs - idx;
              if (segNum <= activeCount) {
                seg.classList.add('active');
              } else {
                seg.classList.remove('active');
              }
            }
          }
        }

        // Sul theme: 3 sequential fill-chambers (docs/RACE_PLASMA_GAUGE_PARADIGMA.md).
        // Tier size is derived from res.maxPlasma (not hardcoded 49/50) so a
        // future BALANCE.maxPlasma tweak keeps the chamber boundaries correct.
        if (isSulTheme && this.elSulChambersByTier.length === 3) {
          const tierSize = res.maxPlasma / 3;
          const tierIndex = Math.min(2, Math.floor(effectivePlasma / tierSize));
          // Side chambers (blue = tier 0/left, green = tier 1/right) stay
          // sequential per-tier, unchanged from before.
          for (let i = 0; i < 2; i++) {
            const chamber = this.elSulChambersByTier[i];
            let localPct = 0;
            if (i < tierIndex) {
              localPct = 100;
              chamber.classList.remove('filling');
              chamber.classList.add('filled');
            } else if (i === tierIndex) {
              localPct = Math.max(0, Math.min(100, ((effectivePlasma - i * tierSize) / tierSize) * 100));
              chamber.classList.add('filling');
              chamber.classList.remove('filled');
            } else {
              chamber.classList.remove('filling', 'filled');
            }
            this.elSulChamberFills[i]?.style.setProperty('--chamber-fill-pct', localPct);
          }

          // Center chamber (purple, index 2) now tracks the OVERALL fill
          // across the whole 0-maxPlasma range instead of only its own
          // final tier - it climbs gradually the entire match rather than
          // sitting empty until the last third. By the last third, both
          // side chambers are already full, so it reads as "only the
          // center still filling up the remaining rest" - see chat request
          // and docs/RACE_PLASMA_GAUGE_PARADIGMA.md.
          const centerChamber = this.elSulChambersByTier[2];
          const centerPct = Math.max(0, Math.min(100, (effectivePlasma / res.maxPlasma) * 100));
          if (centerChamber) {
            centerChamber.classList.toggle('filling', centerPct > 0 && centerPct < 100);
            centerChamber.classList.toggle('filled', centerPct >= 100);
          }
          this.elSulChamberFills[2]?.style.setProperty('--chamber-fill-pct', centerPct);
        }
      }
    }

    // 2B. Gain-pulse: only for discrete jumps (a territory-bonus swing, a
    // future economy-effect card) - the passive per-frame trickle is far
    // smaller than this threshold even at 4x game speed, so it never fires
    // on its own.
    this.detectResourceGain(res.plasma, '_prevPlasma', this.elPlasmaItem, '#00ff88');

    // 3. Render / Update Hand Cards
    this.updateHandUI(res.plasma);

    // 4. Update Live Gamerank Meter (1.0 Hz Throttled, Zero-GC View Layer)
    this.updateLiveGamerank(dt);

    // 4B. Update Congress Decree Indicators & Comic Proclamation Balloons (Every Frame)
    this._updateDecreePills();

    // 5. Update Hacker Intel HUD (Opponent Plasma Capsule & Revealed Hand)
    this._updateHackerIntelHUD();
  }

  updateLiveGamerank(dt) {
    const myTeam = this.game.myTeam || 'western';
    const playerTrack = this._rankBarSegs?.[myTeam];

    if (!playerTrack?.bar) return;

    // Disabled in Exhibition mode (AI vs AI)
    if (this.game.isExhibition) {
      if (!playerTrack.bar.classList.contains('placeholder')) {
        playerTrack.bar.classList.add('placeholder');
      }
      if (playerTrack.tag) playerTrack.tag.style.display = 'none';
      if (this.elLiveSRankAura && this._lastLiveAuraActive) {
        this._lastLiveAuraActive = false;
        this.elLiveSRankAura.classList.remove('active');
      }
      return;
    }

    if (playerTrack.bar.classList.contains('placeholder')) {
      playerTrack.bar.classList.remove('placeholder');
    }
    if (playerTrack.tag) playerTrack.tag.style.display = '';

    // 1.0 Hz throttling accumulator (reduces CPU cycles by 98.3%)
    this._liveRatingTimer = (this._liveRatingTimer || 0) + dt;
    if (this._liveRatingTimer < 1.0) {
      return;
    }
    this._liveRatingTimer = 0;

    const stats = this.game.battlefield?.stats;
    if (!stats) return;

    const teamStats = stats.teams?.[myTeam];
    if (!teamStats) return;

    // Online: only myTeam's stats entry is ever kept live (server-authoritative,
    // see js/net/stateStream.js applyWorldState()) - the opponent's local
    // territoryControlTimeIntegral stays 0, so summing both here would
    // undercount. Use the server's own combined total instead when present.
    const totalTerr = (this.game.isOnline && Number.isFinite(this.game._onlineTotalTerritoryIntegral))
      ? this.game._onlineTotalTerritoryIntegral
      : (stats.teams.western?.territoryControlTimeIntegral || 0) +
        (stats.teams.eastern?.territoryControlTimeIntegral || 0);

    // Zero-allocation projection calculation
    const proj = computeLiveGamerankProjection(teamStats, totalTerr, stats.duration || 0);

    // Dirty-checking before any DOM operations
    if (proj.isWarmup) {
      if (this._lastLiveGrade !== '--') {
        this._lastLiveGrade = '--';
        this._lastLiveScore = 0;
        if (playerTrack.tag) {
          playerTrack.tag.textContent = '--';
          playerTrack.tag.className = 'boss-rank-live-tag';
        }
        playerTrack.fillC && (playerTrack.fillC.style.transform = 'scaleX(0)');
        playerTrack.fillB && (playerTrack.fillB.style.transform = 'scaleX(0)');
        playerTrack.fillA && (playerTrack.fillA.style.transform = 'scaleX(0)');
        playerTrack.fillS && (playerTrack.fillS.style.transform = 'scaleX(0)');
        playerTrack.fillSPlus && (playerTrack.fillSPlus.style.transform = 'scaleX(0)');
        playerTrack.c?.classList.remove('active', 'full');
        playerTrack.b?.classList.remove('active', 'full');
        playerTrack.a?.classList.remove('active', 'full');
        playerTrack.s?.classList.remove('active', 'full');
        playerTrack.sPlus?.classList.remove('active', 'full');
        if (this.elLiveSRankAura && this._lastLiveAuraActive) {
          this._lastLiveAuraActive = false;
          this.elLiveSRankAura.classList.remove('active');
        }
        if (this.elWesternAvatar) this.elWesternAvatar.classList.remove('on-fire', 'on-fire-s-plus');
        if (this.elEasternAvatar) this.elEasternAvatar.classList.remove('on-fire', 'on-fire-s-plus');
      }
      return;
    }

    const effectiveScore = Math.min(100, proj.composite + (this.bonusRankPoints || 0));
    const grade = (this.bonusRankPoints > 0) ? gradeFromComposite(effectiveScore) : proj.grade;
    const score = effectiveScore;

    if (this._lastLiveGrade !== grade || this._lastLiveScore !== score) {
      this._lastLiveGrade = grade;
      this._lastLiveScore = score;

      if (playerTrack.tag) {
        playerTrack.tag.textContent = `${grade} ${score}`;
        playerTrack.tag.className = `boss-rank-live-tag rank-${grade.toLowerCase().replace('+', '-plus').replace('-', '-minus')}`;
      }

      // Proportional Segment Fill calculations (Zero-Reflow GPU scaleX)
      // Dynamically driven by GAMERANK.thresholds
      const t = GAMERANK.thresholds;
      const sFloor = t.sMinus ?? t.s;
      const fillC = Math.max(0, Math.min(1, score / t.b));
      const fillB = Math.max(0, Math.min(1, (score - t.b) / (t.a - t.b)));
      const fillA = Math.max(0, Math.min(1, (score - t.a) / (sFloor - t.a)));
      const fillS = Math.max(0, Math.min(1, (score - sFloor) / (t.sPlus - sFloor)));
      const fillSPlus = Math.max(0, Math.min(1, (score - t.sPlus) / (100 - t.sPlus)));

      if (playerTrack.fillC) playerTrack.fillC.style.transform = `scaleX(${fillC.toFixed(3)})`;
      if (playerTrack.fillB) playerTrack.fillB.style.transform = `scaleX(${fillB.toFixed(3)})`;
      if (playerTrack.fillA) playerTrack.fillA.style.transform = `scaleX(${fillA.toFixed(3)})`;
      if (playerTrack.fillS) playerTrack.fillS.style.transform = `scaleX(${fillS.toFixed(3)})`;
      if (playerTrack.fillSPlus) playerTrack.fillSPlus.style.transform = `scaleX(${fillSPlus.toFixed(3)})`;

      playerTrack.c?.classList.toggle('active', fillC > 0);
      playerTrack.c?.classList.toggle('full', fillC >= 1);
      playerTrack.b?.classList.toggle('active', fillB > 0);
      playerTrack.b?.classList.toggle('full', fillB >= 1);
      playerTrack.a?.classList.toggle('active', fillA > 0);
      playerTrack.a?.classList.toggle('full', fillA >= 1);
      playerTrack.s?.classList.toggle('active', fillS > 0);
      playerTrack.s?.classList.toggle('full', fillS >= 1);
      playerTrack.sPlus?.classList.toggle('active', fillSPlus > 0);
      playerTrack.sPlus?.classList.toggle('full', fillSPlus >= 1);

      // S-Rank "On Fire" Avatar Flame & Golden Ambient Aura
      const isSRank = (grade === 'S' || grade === 'S+');
      const isSPlus = (grade === 'S+');
      const playerAvatar = myTeam === 'eastern' ? this.elEasternAvatar : this.elWesternAvatar;
      const enemyAvatar = myTeam === 'eastern' ? this.elWesternAvatar : this.elEasternAvatar;

      if (playerAvatar) {
        playerAvatar.classList.toggle('on-fire', isSRank);
        playerAvatar.classList.toggle('on-fire-s-plus', isSPlus);
      }
      if (enemyAvatar) {
        enemyAvatar.classList.remove('on-fire', 'on-fire-s-plus');
      }

      if (this._lastLiveAuraActive !== isSRank) {
        this._lastLiveAuraActive = isSRank;
        if (this.elLiveSRankAura) {
          const isLowQuality = this.game?.quality === 'low';
          if (isSRank && !isLowQuality) {
            this.elLiveSRankAura.classList.add('active');
          } else {
            this.elLiveSRankAura.classList.remove('active');
          }
        }
      }
    }
  }

  _updateDecreePills() {
    const ms = this.game.battlefield?.moduleSystem;
    const teams = ['western', 'eastern'];
    for (let t = 0; t < 2; t++) {
      const team = teams[t];
      const pill = team === 'western' ? this.elWesternDecreePill : this.elEasternDecreePill;
      const balloon = team === 'western' ? this.elWesternDecreeBalloon : this.elEasternDecreeBalloon;
      if (!pill) continue;

      const state = ms?.getCongressState ? ms.getCongressState(team) : null;
      if (!state || !state.active) {
        if (pill._lastDecreeActive !== false) {
          pill._lastDecreeActive = false;
          if (!pill.classList.contains('hidden')) pill.classList.add('hidden');
          if (balloon && !balloon.classList.contains('hidden')) balloon.classList.add('hidden');
        }
        continue;
      }

      if (pill._lastDecreeActive !== true) {
        pill._lastDecreeActive = true;
        if (pill.classList.contains('hidden')) pill.classList.remove('hidden');
      }

      const effectEl = team === 'western' ? this.elWesternDecreeEffect : this.elEasternDecreeEffect;
      const progEl = team === 'western' ? this.elWesternDecreeProgress : this.elEasternDecreeProgress;

      const buff = state.buff;
      const color = buff?.color || '#00ff88';
      if (pill._lastDecreeColor !== color) {
        pill.style.setProperty('--decree-color', color);
        pill._lastDecreeColor = color;
      }

      const effectText = buff?.hudEffect || '';
      if (effectEl && effectEl.textContent !== effectText) {
        effectEl.textContent = effectText;
      }

      if (progEl) {
        const pct = Math.round(Math.max(0, Math.min(100, (state.ticksRemaining / state.ticksTotal) * 100)) * 10) / 10;
        if (progEl._lastPct !== pct) {
          progEl.style.width = `${pct}%`;
          progEl._lastPct = pct;
        }
      }

      // Proclamation Shout Balloon anchored under the decree indicator
      const introEl = team === 'western' ? this.elWesternShoutIntro : this.elEasternShoutIntro;
      const punchEl = team === 'western' ? this.elWesternShoutPunchline : this.elEasternShoutPunchline;
      const bubble = ms?.fx?.[team]?.congress_module?.bubble;

      if (balloon) {
        if (bubble && bubble.timer > 0) {
          if (balloon.classList.contains('hidden')) {
            balloon.classList.remove('hidden');
          }
          const balloonColor = bubble.color || color;
          if (balloon._lastDecreeColor !== balloonColor) {
            balloon.style.setProperty('--decree-color', balloonColor);
            balloon._lastDecreeColor = balloonColor;
          }

          const elapsed = Math.max(0, (bubble.total || 3.0) - bubble.timer);

          if (elapsed > 2.5) {
            balloon.classList.add('fading');
          } else {
            balloon.classList.remove('fading');
          }

          // Phase 1: Typewriter reveal of Intro (0.04s - 0.48s)
          if (introEl) {
            if (elapsed < 0.04) {
              if (introEl.textContent !== '') introEl.textContent = '';
            } else {
              const typeP = Math.min(1, (elapsed - 0.04) / 0.44);
              const charCount = Math.min(bubble.intro.length, Math.max(1, Math.ceil(typeP * bubble.intro.length)));
              const currentIntro = bubble.intro.slice(0, charCount);
              if (introEl.textContent !== currentIntro) {
                introEl.textContent = currentIntro;
              }
            }
          }

          // Phase 2: Bold Punchline Stamp (at 0.70s)
          if (punchEl) {
            if (elapsed >= 0.70) {
              if (!punchEl.classList.contains('stamped')) {
                punchEl.classList.add('stamped');
              }
              if (punchEl.textContent !== bubble.decree) {
                punchEl.textContent = bubble.decree;
                if (bubble.decree.length > 18) {
                  punchEl.style.fontSize = '11.5px';
                } else if (bubble.decree.length > 14) {
                  punchEl.style.fontSize = '13px';
                } else {
                  punchEl.style.fontSize = '15px';
                }
              }
            } else {
              punchEl.classList.remove('stamped');
              if (punchEl.textContent !== '') {
                punchEl.textContent = '';
              }
            }
          }
        } else {
          if (!balloon.classList.contains('hidden')) {
            balloon.classList.add('hidden');
            balloon.classList.remove('fading');
          }
          if (introEl && introEl.textContent !== '') {
            introEl.textContent = '';
          }
          if (punchEl) {
            punchEl.classList.remove('stamped');
            punchEl.textContent = '';
          }
        }
      }
    }
  }

  addBonusRankPoints(pts) {
    this.bonusRankPoints = (this.bonusRankPoints || 0) + pts;
    this._liveRatingTimer = 1.0; // trigger recalculation
  }

  getLiveGrade() {
    return this._lastLiveGrade || '--';
  }

  // 🌐 Human Hacker Intel HUD (Opponent Plasma Capsule & Revealed Hand)
  _initHackerIntelElements() {
    if (typeof document === 'undefined') return;
    this._hackerIntelClusters = {};
    const teams = ['western', 'eastern'];
    for (const team of teams) {
      const cluster = document.getElementById(`${team}-avatar-cluster`);
      if (cluster && !cluster.querySelector('.hacker-intel-cluster')) {
        const wrap = document.createElement('div');
        wrap.className = 'hacker-intel-cluster hidden';
        wrap.innerHTML = `
          <div class="opponent-plasma-capsule hidden">
            <div class="opp-plasma-fluid-fill">
              <div class="plasma-wave-back"></div>
              <div class="plasma-wave-front"></div>
              <div class="plasma-surface-line"></div>
            </div>
            <div class="opp-capsule-glass">
              <span class="opp-plasma-num">0</span>
            </div>
          </div>
          <div class="opponent-revealed-hand hidden"></div>
        `;
        cluster.appendChild(wrap);
        this._hackerIntelClusters[team] = {
          wrap,
          capsule: wrap.querySelector('.opponent-plasma-capsule'),
          fluidFill: wrap.querySelector('.opp-plasma-fluid-fill'),
          plasmaNum: wrap.querySelector('.opp-plasma-num'),
          revealedHand: wrap.querySelector('.opponent-revealed-hand'),
          timerFill: null,
          timerTrack: null,
          _lastHandHtml: '',
          _lastOppPct: -1
        };
      } else if (cluster) {
        const wrap = cluster.querySelector('.hacker-intel-cluster');
        this._hackerIntelClusters[team] = {
          wrap,
          capsule: wrap?.querySelector('.opponent-plasma-capsule'),
          fluidFill: wrap?.querySelector('.opp-plasma-fluid-fill'),
          plasmaNum: wrap?.querySelector('.opp-plasma-num'),
          revealedHand: wrap?.querySelector('.opponent-revealed-hand'),
          timerFill: null,
          timerTrack: null,
          _lastHandHtml: '',
          _lastOppPct: -1
        };
      }
    }
  }

  _updateHackerIntelHUD() {
    if (!this._hackerIntelClusters) return;
    const myTeam = this.game.myTeam || this.game.battlefield?.playerTeam || 'western';
    const oppTeam = myTeam === 'western' ? 'eastern' : 'western';
    const oppCluster = this._hackerIntelClusters[oppTeam];
    const myCluster = this._hackerIntelClusters[myTeam];

    // Hide intel on own player cluster (intel belongs under the opponent)
    if (myCluster?.wrap && myCluster._lastIntelMode !== 'hidden') {
      myCluster._lastIntelMode = 'hidden';
      myCluster.wrap.classList.add('hidden');
      myCluster.capsule?.classList.add('hidden');
      myCluster.revealedHand?.classList.add('hidden');
    }

    if (!oppCluster?.wrap) return;

    // Check if player has an active connected Hacker
    // Plain loop (this runs every HUD update - a find() with a closure would
    // allocate one per frame even in matches without any Hacker).
    let activeHacker = null;
    const bfUnits = this.game.battlefield?.units;
    if (bfUnits) {
      for (let i = 0; i < bfUnits.length; i++) {
        const u = bfUnits[i];
        if (u && !u.isDead && u.team === myTeam && u.isHacking && u.hackerTargetId &&
            (u.isHacker || u.name === 'Hacker' || u.cardData?.unitType === 'Hacker')) {
          activeHacker = u;
          break;
        }
      }
    }

    const isAiHandEnabled = this.game?.isShowAiHandEnabled?.();

    if (activeHacker || isAiHandEnabled) {
      oppCluster.wrap.classList.remove('hidden');

      const lockCycle = activeHacker ? ((activeHacker.hackerLockTimer || 0) % 15.0) : 0;
      const isHackerRevealActive = activeHacker && ((activeHacker.handRevealTimer > 0) || (lockCycle >= 9.8 && lockCycle < 15.0));
      const isRevealActive = isHackerRevealActive || isAiHandEnabled;

      // Mutual exclusion: during the 5s hand-reveal window, hide plasma capsule to avoid overlap
      if (isRevealActive) {
        oppCluster.capsule?.classList.add('hidden');
        oppCluster.revealedHand?.classList.remove('hidden');

        const oppHand = (this.game.cardSystem?.hands?.[oppTeam]) ||
                        (this.game.wsClient?.opponentHand) ||
                        (this.game.wsMatchClient?.opponentHand) ||
                        [];
        const handSize = Math.max(
          oppHand.length,
          this.game.cardSystem?.maxHandSize || BALANCE?.maxHandSize || 5
        );
        // Cheap signature first (card names per slot): the HTML - five
        // getCardByName() + getCardThumbUrl() lookups and a ~1 KB template
        // string - is only built when the revealed hand actually changed,
        // not on every frame of the 5 s reveal window.
        let handKey = '';
        for (let i = 0; i < handSize; i++) {
          let card = oppHand[i];
          if ((!card || !card.name || card.name === '···') && this.game.wsClient?.opponentHand?.[i]?.name && this.game.wsClient.opponentHand[i].name !== '···') {
            card = this.game.wsClient.opponentHand[i];
          }
          handKey += (card && card.name && card.name !== '···') ? `${card.name}|` : '-|';
        }
        if (oppCluster._lastHandHtml !== handKey) {
          oppCluster._lastHandHtml = handKey;
          let handHtml = '<div class="opp-hand-cards-row">';
          for (let i = 0; i < handSize; i++) {
            let card = oppHand[i];
            if ((!card || !card.name || card.name === '···') && this.game.wsClient?.opponentHand?.[i]?.name && this.game.wsClient.opponentHand[i].name !== '···') {
              card = this.game.wsClient.opponentHand[i];
            }
            if (card && card.name && card.name !== '···') {
              const rawCard = getCardByName(card.name) || card;
              // 's' (224 px) thumbs: the tiles are 85x118 css px, so the 448 px
              // card thumb only cost decode time/memory on the reveal frame.
              const imgPath = getCardThumbUrl(rawCard.images?.[0] || '1_hacker.png', HACKER_REVEAL_THUMB_SIZE);
              handHtml += `
              <div class="opp-revealed-card" title="${card.name}">
                <img src="${imgPath}" alt="${card.name}" decoding="async">
                <span class="opp-card-cost">${rawCard.cost ?? 0}</span>
              </div>
            `;
            } else {
              handHtml += `<div class="opp-revealed-card opp-empty-slot"></div>`;
            }
          }
          handHtml += '</div><div class="opp-hand-timer-track"><div class="opp-hand-timer-fill"></div></div>';
          oppCluster.revealedHand.innerHTML = handHtml;
          oppCluster.timerFill = oppCluster.revealedHand.querySelector?.('.opp-hand-timer-fill') || null;
          oppCluster.timerTrack = oppCluster.revealedHand.querySelector?.('.opp-hand-timer-track') || null;
        }
        if (!oppCluster.timerTrack && oppCluster.revealedHand?.querySelector) {
          oppCluster.timerTrack = oppCluster.revealedHand.querySelector('.opp-hand-timer-track') || null;
        }
        if (activeHacker) {
          const effectiveRevealTimer = activeHacker.handRevealTimer > 0 ? activeHacker.handRevealTimer : Math.max(0, 15.0 - lockCycle);
          const timerRatio = Math.max(0, Math.min(1, effectiveRevealTimer / 5.0));
          if (oppCluster.timerFill) {
            oppCluster.timerFill.style.transform = `scaleX(${timerRatio.toFixed(3)})`;
          }
          if (oppCluster.timerTrack) {
            oppCluster.timerTrack.style.display = '';
          }
        } else {
          if (oppCluster.timerTrack) {
            oppCluster.timerTrack.style.display = 'none';
          }
        }
        if (oppCluster._lastIntelMode !== 'reveal') {
          oppCluster._lastIntelMode = 'reveal';
          this.game?.tauntManager?.syncCutInVerticalOffset?.();
        }
      } else {
        // Outside the reveal window: hide revealed hand and display fixed-width plasma capsule
        oppCluster.revealedHand?.classList.add('hidden');
        oppCluster._lastHandHtml = '';
        oppCluster.timerFill = null;

        oppCluster.capsule?.classList.remove('hidden');
        const oppPlasma = this.game.economy?.resources?.[oppTeam]?.plasma ??
                          this.game.cardSystem?.plasmas?.[oppTeam] ??
                          this.game.teammatePlasma ?? 0;
        const oppMaxPlasma = this.game.economy?.resources?.[oppTeam]?.maxPlasma || BALANCE?.maxPlasma || 150;
        const oppPct = Math.max(0, Math.min(100, Math.round((oppPlasma / oppMaxPlasma) * 100)));
        if (oppCluster._lastOppPct !== oppPct) {
          oppCluster._lastOppPct = oppPct;
          const pctStr = `${oppPct}%`;
          oppCluster.capsule?.style?.setProperty?.('--opp-plasma-pct', pctStr);
          oppCluster.fluidFill?.style?.setProperty?.('--opp-plasma-pct', pctStr);
        }
        if (oppCluster.plasmaNum) {
          const oppPlasmaFloor = Math.floor(oppPlasma);
          if (oppCluster._lastOppPlasmaFloor !== oppPlasmaFloor) {
            oppCluster._lastOppPlasmaFloor = oppPlasmaFloor;
            oppCluster.plasmaNum.textContent = `${oppPlasmaFloor}`;
          }
        }
        if (oppCluster._lastIntelMode !== 'capsule') {
          oppCluster._lastIntelMode = 'capsule';
          oppCluster.wrap.classList.remove('hidden');
          oppCluster.revealedHand?.classList.add('hidden');
          oppCluster.capsule?.classList.remove('hidden');
          this.game?.tauntManager?.syncCutInVerticalOffset?.();
        }
      }
    } else if (oppCluster._lastIntelMode !== 'hidden') {
      oppCluster._lastIntelMode = 'hidden';
      oppCluster.wrap.classList.add('hidden');
      oppCluster.capsule?.classList.add('hidden');
      oppCluster.revealedHand?.classList.add('hidden');
      oppCluster._lastHandHtml = '';
      oppCluster._lastOppPlasmaFloor = -1;
      oppCluster.timerFill = null;
      this.game?.tauntManager?.syncCutInVerticalOffset?.();
    }
  }

  resetLiveGamerank() {
    this.bonusRankPoints = 0;
    this._liveRatingTimer = 0;
    this._lastLiveGrade = null;
    this._lastLiveScore = -1;
    this._lastLiveAuraActive = false;
    for (const team of ['western', 'eastern']) {
      const track = this._rankBarSegs?.[team];
      if (!track) continue;
      if (track.tag) {
        track.tag.textContent = '--';
        track.tag.className = 'boss-rank-live-tag';
      }
      track.fillC && (track.fillC.style.transform = 'scaleX(0)');
      track.fillB && (track.fillB.style.transform = 'scaleX(0)');
      track.fillA && (track.fillA.style.transform = 'scaleX(0)');
      track.fillS && (track.fillS.style.transform = 'scaleX(0)');
      track.fillSPlus && (track.fillSPlus.style.transform = 'scaleX(0)');
      track.c?.classList.remove('active', 'full');
      track.b?.classList.remove('active', 'full');
      track.a?.classList.remove('active', 'full');
      track.s?.classList.remove('active', 'full');
      track.sPlus?.classList.remove('active', 'full');
    }
    if (this.elWesternAvatar) this.elWesternAvatar.classList.remove('on-fire', 'on-fire-s-plus');
    if (this.elEasternAvatar) this.elEasternAvatar.classList.remove('on-fire', 'on-fire-s-plus');
    if (this.elLiveSRankAura) this.elLiveSRankAura.classList.remove('active');

    this._hasPlayedSpecial = { western: false, eastern: false };
    this.elWesternAvatar?.classList.remove('has-special-edition', 'special-badge-pulse');
    this.elEasternAvatar?.classList.remove('has-special-edition', 'special-badge-pulse');
  }

  // Called from js/commands/commandProcessor.js's _play() whenever the
  // OPPONENT plays a Special Edition card (Gold etc.) - see
  // docs/CARD_AUTHORING_GUIDE.md "Special Editions". `.boss-avatar.western`/
  // `.eastern` are fixed to the actual simulation team (see
  // Game.updateBaseLabels() in js/main.js), never swapped for "me"/"enemy",
  // so `team` maps directly to one of the two avatar elements.
  pulseSpecialBadge(team) {
    const el = team === 'western' ? this.elWesternAvatar : this.elEasternAvatar;
    if (!el) return;
    if (!this._hasPlayedSpecial) this._hasPlayedSpecial = { western: false, eastern: false };
    this._hasPlayedSpecial[team] = true;
    el.classList.add('has-special-edition');
    el.classList.remove('special-badge-pulse');
    void el.offsetWidth; // reflow, restarts the pulse animation on repeat plays
    el.classList.add('special-badge-pulse');
  }

  detectResourceGain(value, prevKey, el, pulseColor) {
    const prev = this[prevKey];
    this[prevKey] = value;
    if (prev === null || !el) return;
    if (value - prev >= 1) {
      el.style.setProperty('--pulse-color', pulseColor);
      el.classList.remove('gain-pulse');
      // Force reflow so re-adding the class restarts the animation even if
      // it's already mid-pulse from a rapid second gain.
      void el.offsetWidth;
      el.classList.add('gain-pulse');
    }
  }


  shakeInsufficientFunds(cardSlotEl) {
    const capsuleEl = this.elPlasmaCapsule || document.getElementById('plasma-capsule-container');
    if (capsuleEl) {
      capsuleEl.classList.remove('insufficient-shake', 'insufficient-alert');
      void capsuleEl.offsetWidth;
      capsuleEl.classList.add('insufficient-shake', 'insufficient-alert');
      setTimeout(() => {
        capsuleEl.classList.remove('insufficient-shake', 'insufficient-alert');
      }, 650);
    }
    if (this.elPlasmaItem) {
      this.elPlasmaItem.classList.remove('insufficient-shake');
      void this.elPlasmaItem.offsetWidth;
      this.elPlasmaItem.classList.add('insufficient-shake');
      setTimeout(() => this.elPlasmaItem?.classList.remove('insufficient-shake'), 450);
    }
    const targetEl = cardSlotEl?.closest('.card-wrapper') || cardSlotEl;
    if (targetEl) {
      targetEl.classList.remove('slot-deploying', 'drag-source-hidden');
      const s = targetEl.querySelector('.card-slot') || targetEl;
      s.classList.remove('card-deploying', 'card-deploy-window');
      s.style.pointerEvents = '';
      targetEl.classList.remove('insufficient-shake');
      void targetEl.offsetWidth;
      targetEl.classList.add('insufficient-shake');
      setTimeout(() => targetEl?.classList.remove('insufficient-shake'), 450);
    }
  }

  // High-impact, AAA-inspired Triple Melt Fusion Animation
  // Plays golden supernova burst, shockwave ring, 360-degree particle sparks,
  // card slam, holographic light sheen sweep, floating badge and dock shake.
  playMeltAnimation(cardSlotEl) {
    const targetWrapper = cardSlotEl?.closest('.card-wrapper') || cardSlotEl?.parentElement;
    if (!targetWrapper) return;

    // The convergence copies have arrived - clear the convergence stage
    this._clearStage();

    const targetSlot = targetWrapper.querySelector('.card-slot');
    if (!targetSlot) return;

    // 1. Slam/Flip the fused card directly in its hand slot (identical to Reconsider):
    // Runs on .card-slot within its natural hand position - no clone on #card-stage,
    // avoiding WebKit 2D/3D matrix flattening hitches and avoiding hidden-slot timeouts.
    targetWrapper.classList.remove('card-melt-flash');
    targetSlot.classList.remove('card-melt-flash');
    void targetSlot.offsetWidth;
    targetWrapper.classList.add('card-melt-flash');
    targetSlot.classList.add('card-melt-flash');
    setTimeout(() => {
      targetWrapper?.classList.remove('card-melt-flash');
      targetSlot?.classList.remove('card-melt-flash');
    }, 2400);

    // 2. Holographic light sheen sweep across the card artwork:
    // Timed with the 3D flip reveal so the beam sweeps across the front
    // of the card right as it rotates into full frontal view (~180ms in).
    const cardInner = targetSlot.querySelector('.card-inner');
    if (cardInner) {
      setTimeout(() => {
        if (cardInner.isConnected) cardInner.classList.add('card-shimmer-sweep');
      }, 180);
      setTimeout(() => cardInner?.classList.remove('card-shimmer-sweep'), 1500);
    }

    // 3. Dock micro-shake for physical weight and impact feel
    if (this.elHandContainer) {
      this.elHandContainer.classList.remove('melt-dock-impact-shake');
      void this.elHandContainer.offsetWidth;
      this.elHandContainer.classList.add('melt-dock-impact-shake');
      setTimeout(() => this.elHandContainer?.classList.remove('melt-dock-impact-shake'), 400);
    }

    // 4. Construct high-performance GPU particle & shockwave overlay on the stage spot:
    // Exactly like Reconsider's _stageSpot(), so flare and rings can expand past the
    // card without clipping, while leaving the card itself in the DOM.
    const fxHost = this._stageSpot(targetWrapper) || targetWrapper;
    const existingOverlay = fxHost.querySelector('.melt-fx-overlay');
    if (existingOverlay) existingOverlay.remove();

    const overlayEl = document.createElement('div');
    overlayEl.className = 'melt-fx-overlay';

    const haloEl = document.createElement('div');
    haloEl.className = 'melt-glow-halo';
    overlayEl.appendChild(haloEl);

    // Supernova Lens Flare Burst
    const flareEl = document.createElement('div');
    flareEl.className = 'melt-supernova-flare';
    overlayEl.appendChild(flareEl);

    // Expanding Golden Shockwave Ring
    const shockwaveEl = document.createElement('div');
    shockwaveEl.className = 'melt-shockwave-ring';
    overlayEl.appendChild(shockwaveEl);

    // 360-degree Particle Spark Burst
    const sparkCount = (window.innerWidth || 0) < 700 ? 12 : 20;
    const sparkGlyphs = ['✦', '★', '◆', '⚡', '•'];
    for (let i = 0; i < sparkCount; i++) {
      const spark = document.createElement('span');
      spark.className = 'melt-particle-spark';
      spark.innerText = sparkGlyphs[i % sparkGlyphs.length];

      const angle = (i / sparkCount) * (Math.PI * 2) + (Math.random() * 0.3 - 0.15);
      const distance = 55 + Math.random() * 65;
      const tx = Math.cos(angle) * distance;
      const ty = Math.sin(angle) * distance;
      const scale = 0.8 + Math.random() * 0.5;
      const delay = Math.random() * 60;

      spark.style.setProperty('--tx', `${tx.toFixed(1)}px`);
      spark.style.setProperty('--ty', `${ty.toFixed(1)}px`);
      spark.style.setProperty('--s', `${scale.toFixed(2)}`);
      spark.style.animationDelay = `${delay.toFixed(0)}ms`;

      overlayEl.appendChild(spark);
    }

    // Floating Celebratory Sci-Fi Badge - on the stage unscaled
    const isPulpMelt = !!this.game?.cardSystem?.lastMeltSlot?.isPulp;
    if (isPulpMelt) {
      overlayEl.classList.add('pulp-fx-overlay');
      shockwaveEl.classList.add('pulp-shockwave-ring');
    }

    const badgeEl = document.createElement('div');
    badgeEl.className = isPulpMelt ? 'melt-floating-badge pulp-floating-badge' : 'melt-floating-badge';
    if (isPulpMelt) {
      badgeEl.innerHTML = '<span class="badge-bracket">[</span> SUL CATALYST // GENETIC FUSION <span class="badge-bracket">]</span>';
    } else {
      badgeEl.innerHTML = `<span class="badge-icon">${getIconSvg('energy', 14)}</span> HYPER UPGRADE <span class="badge-icon">${getIconSvg('energy', 14)}</span>`;
    }
    const captionRect = targetWrapper.querySelector('.card-scale')?.getBoundingClientRect();
    if (!this._stageCaption(badgeEl, captionRect)) overlayEl.appendChild(badgeEl);

    fxHost.appendChild(overlayEl);
    setTimeout(() => {
      if (overlayEl.parentNode) overlayEl.remove();
      if (fxHost !== targetWrapper && fxHost.parentNode) fxHost.remove();
      if (badgeEl.parentNode) badgeEl.remove();
    }, 2200);
  }

  // Stage 1 of Concentration VFX: Gravitational core and suction of sibling cards
  _startConcentrationVFX(slotIndex) {
    if (!this.elHandContainer) return;
    this._concentrationSuctionActive = true;
    this._concentrationSuctionStart = performance.now();

    // Runs on the stage for the same reason the melt does: every sibling
    // flies out of its own slot on the first frame, and the vortex card
    // swells past its frame - inside the hand both are clipped at the card's
    // edge. Falls back to animating in place if the stage is unavailable.
    const originWrapper = this.elHandContainer.children[slotIndex];
    if (originWrapper) {
      const targetRect = originWrapper.querySelector('.card-scale')?.getBoundingClientRect()
        || originWrapper.getBoundingClientRect();
      const hidden = new Set();

      Array.from(this.elHandContainer.children).forEach((wrapper, sIdx) => {
        const staged = this._stageCard(wrapper);
        const slot = staged ? staged.slot : wrapper.querySelector('.card-slot');
        if (!slot) return;
        if (staged) hidden.add(sIdx);

        if (sIdx === slotIndex) {
          slot.classList.add('concentration-vortex-active');
          return;
        }
        const from = (wrapper.querySelector('.card-scale') || wrapper).getBoundingClientRect();
        const k = staged ? staged.k : this._handCardScale(wrapper);
        // Design pixels, not screen pixels - see _handCardScale().
        const dx = (targetRect.left - from.left) / k;
        slot.style.setProperty('--implode-target-x', `${dx.toFixed(1)}px`);
        slot.style.setProperty('--implode-rot', `${dx < 0 ? -18 : 18}deg`);
        slot.classList.add('concentration-imploding');
      });

      if (hidden.size > 0) {
        this._stageHiddenSlots = hidden;
        // Handed over by playConcentrationAnimation() once the new hand has
        // been dealt - with watchdog safety net so it can never hang.
        this._stageOwner = 'concentration-implosion';
        this._armConcentrationWatchdog();
      }
    }

    this.game.audio?.playConcentrationCharge?.();
  }

  // Stage 2 & 3 Concentration Spell VFX: Supernova Flare, Dual Shockwaves, 360° Sparks & Card Deal-In
  playConcentrationAnimation(slotIndex, badgeText = 'RECONSIDER') {
    if (!this.elHandContainer) return;
    clearTimeout(this._concentrationWatchdogTimer);
    this._concentrationWatchdogTimer = null;
    this._concentrationSuctionActive = false;
    this._concentrationSuctionStart = 0;
    const originWrapper = this.elHandContainer.children[slotIndex] || this.elHandContainer.children[2] || this.elHandContainer.children[0];
    const isLowQuality = this.game.performance?.quality === 'low';

    // The imploding copies have done their work and the new hand is dealt -
    // give the real cards back before the burst plays over them.
    if (this._stageOwner === 'concentration-implosion') this._clearStage();

    // 1. Construct Supernova Lens Flare, Dual Shockwave Rings & 360° Particle Sparks
    if (originWrapper && !isLowQuality) {
      const existingOverlay = originWrapper.querySelector('.concentration-fx-overlay');
      if (existingOverlay) existingOverlay.remove();

      const overlayEl = document.createElement('div');
      overlayEl.className = 'concentration-fx-overlay';

      // Supernova Lens Flare Burst
      const flareEl = document.createElement('div');
      flareEl.className = 'concentration-supernova-flare';
      overlayEl.appendChild(flareEl);

      // Primary Violet Shockwave Ring
      const ring1 = document.createElement('div');
      ring1.className = 'concentration-shockwave-ring-primary';
      overlayEl.appendChild(ring1);

      // Secondary Cyan Shockwave Ring
      const ring2 = document.createElement('div');
      ring2.className = 'concentration-shockwave-ring-secondary';
      overlayEl.appendChild(ring2);

      // 360-degree Particle Spark Burst (28 psionic sparks)
      const sparkCount = 28;
      const sparkGlyphs = ['✦', '★', '◆', '⚡', '•', '✧'];
      for (let i = 0; i < sparkCount; i++) {
        const spark = document.createElement('span');
        spark.className = 'concentration-particle-spark';
        spark.innerText = sparkGlyphs[i % sparkGlyphs.length];

        const angle = (i / sparkCount) * (Math.PI * 2) + (Math.random() * 0.25 - 0.12);
        const distance = 65 + Math.random() * 85;
        const tx = Math.cos(angle) * distance;
        const ty = Math.sin(angle) * distance;
        const scale = 0.9 + Math.random() * 0.7;
        const delay = Math.random() * 70;

        spark.style.setProperty('--tx', `${tx.toFixed(1)}px`);
        spark.style.setProperty('--ty', `${ty.toFixed(1)}px`);
        spark.style.setProperty('--s', `${scale.toFixed(2)}`);
        spark.style.animationDelay = `${delay.toFixed(0)}ms`;

        overlayEl.appendChild(spark);
      }

      // Floating Sci-Fi Celebratory Badge - mounted unscaled on the stage
      // for the same reason as the melt's (see _stageCaption).
      let badgeLabel = 'RECONSIDER';
      if (typeof badgeText === 'string') {
        const clean = badgeText.trim().toLowerCase();
        if (clean === 'mirror' || clean === 'mental_extraction') {
          badgeLabel = 'MIRROR';
        } else if (clean === 'reconsider' || clean === 'concentration') {
          badgeLabel = 'RECONSIDER';
        } else if (clean) {
          badgeLabel = badgeText.trim().toUpperCase();
        }
      }
      const badgeEl = document.createElement('div');
      badgeEl.className = 'concentration-floating-badge';
      badgeEl.innerHTML = `<span class="badge-icon">${getIconSvg('energy', 14)}</span> ${badgeLabel} <span class="badge-icon">${getIconSvg('energy', 14)}</span>`;
      const captionRect = originWrapper.querySelector('.card-scale')?.getBoundingClientRect();
      if (!this._stageCaption(badgeEl, captionRect)) overlayEl.appendChild(badgeEl);

      // On the stage, so the flare and the rings can spread past the card
      // they burst from instead of being cut off at its frame.
      const fxHost = this._stageSpot(originWrapper) || originWrapper;
      fxHost.appendChild(overlayEl);
      setTimeout(() => {
        if (overlayEl.parentNode) overlayEl.remove();
        if (fxHost !== originWrapper && fxHost.parentNode) fxHost.remove();
        // The badge is mounted on the stage rather than inside the overlay
        // (see _stageCaption), so removing the overlay no longer takes it
        // with it - and unlike the melt, nothing clears the stage after a
        // Reconsider / Mental Extraction, so it would sit there invisible but forever.
        if (badgeEl.parentNode) badgeEl.remove();
      }, 2200);
    }

    // 2. Heavy dock quake
    if (this.elHandContainer) {
      this.elHandContainer.classList.remove('concentration-dock-impact-shake');
      void this.elHandContainer.offsetWidth;
      this.elHandContainer.classList.add('concentration-dock-impact-shake');
      setTimeout(() => this.elHandContainer?.classList.remove('concentration-dock-impact-shake'), 500);
    }

    // 3. Staggered burst-enter and holographic shimmer for all newly drawn cards
    const slots = this.elHandContainer.querySelectorAll('.card-slot.filled');
    slots.forEach((slot, idx) => {
      slot.classList.remove('concentration-burst-enter');
      slot.style.animationDelay = `${idx * 45}ms`;
      void slot.offsetWidth;
      slot.classList.add('concentration-burst-enter');

      const cardInner = slot.querySelector('.card-inner');
      if (cardInner) {
        cardInner.classList.remove('card-shimmer-sweep');
        cardInner.style.animationDelay = `${idx * 45 + 100}ms`;
        void cardInner.offsetWidth;
        cardInner.classList.add('card-shimmer-sweep');
        setTimeout(() => {
          cardInner.classList.remove('card-shimmer-sweep');
          cardInner.style.animationDelay = '';
        }, 1400);
      }

      setTimeout(() => {
        slot.classList.remove('concentration-burst-enter');
        slot.style.animationDelay = '';
      }, 600);
    });

    // 4. Audio impact
    this.game.audio?.playConcentrationBurst?.();
  }

  updateHandUI(currentPlasma) {
    if (!this.elHandContainer) return;
    const hand = this.game.cardSystem.hands[this.game.myTeam];

    // A PLAY or DRAW command that failed on insufficient plasma (rejected
    // inside CommandProcessor, one tick after the click) still gets the
    // same one-shot shake the old synchronous click handler used to
    // trigger. Two independent seq/slot pairs since a PLAY failure and a
    // DRAW failure can never land on the same slot in the same tick (a
    // slot is either occupied, so only PLAY applies, or empty, so only
    // DRAW applies) but can each have their own stale sequence otherwise.
    const cs = this.game.cardSystem;
    if (cs.playErrorSeq && cs.playErrorSeq !== this.lastSeenPlayErrorSeq) {
      this.lastSeenPlayErrorSeq = cs.playErrorSeq;
      if (cs.lastPlayErrorSlot?.team === this.game.myTeam) {
        this._exitDeployWindow(cs.lastPlayErrorSlot.slot);
        const wrapperDiv = this.elHandContainer.children[cs.lastPlayErrorSlot.slot];
        const slotDiv = wrapperDiv?.querySelector('.card-slot');
        if (slotDiv) this.shakeInsufficientFunds(slotDiv);
      }
    }
    if (cs.drawErrorSeq && cs.drawErrorSeq !== this.lastSeenDrawErrorSeq) {
      this.lastSeenDrawErrorSeq = cs.drawErrorSeq;
      if (cs.lastDrawErrorSlot?.team === this.game.myTeam) {
        this._exitDeployWindow(cs.lastDrawErrorSlot.slot);
        const wrapperDiv = this.elHandContainer.children[cs.lastDrawErrorSlot.slot];
        const slotDiv = wrapperDiv?.querySelector('.card-slot');
        if (slotDiv) this.shakeInsufficientFunds(slotDiv);
      }
    }

    // Close any window whose slot has since changed hands - the command
    // reached the simulation. For a play that means the card is gone; for a
    // draw it means a card (real or the pending placeholder) has landed.
    // One rule covers both. DEPLOY_WINDOW_MAX_MS is the fallback for when
    // the command never arrives at all.
    if (this._deployWindowSlots && this._deployWindowSlots.size > 0) {
      const nowMs = performance.now();
      for (const [sIdx, entry] of this._deployWindowSlots.entries()) {
        const liveCard = hand[sIdx];
        const idNow = liveCard ? (liveCard.instanceId || liveCard.id) : null;
        if (idNow !== entry.slotIdAtClick || (nowMs - entry.t) > DEPLOY_WINDOW_MAX_MS) {
          this._exitDeployWindow(sIdx);
        }
      }
    }

    // Zero-allocation hand change detection (avoids allocating 240-300 strings/sec on heap)
    let handChanged = false;
    const handLen = hand?.length || this.game?.cardSystem?.maxHandSize || BALANCE?.maxHandSize || 5;
    if (!this._lastHandCardIds || this._lastHandCardIds.length !== handLen) {
      this._lastHandCardIds = new Array(handLen).fill(null);
      this._lastHandCardInstances = new Array(handLen).fill(null);
    }
    for (let i = 0; i < handLen; i++) {
      const c = hand[i];
      const cid = c ? c.id : null;
      const cinst = c ? (c.instanceId ?? null) : null;
      if (this._lastHandCardIds[i] !== cid || this._lastHandCardInstances[i] !== cinst) {
        handChanged = true;
        this._lastHandCardIds[i] = cid;
        this._lastHandCardInstances[i] = cinst;
      }
    }
    if (handChanged) {
      this._handDirty = true;
    }

    // Fail-safe: if concentration stage is held longer than 1200ms without resolving, force-clear it
    if (this._stageOwner === 'concentration-implosion' && this._concentrationSuctionStart && (performance.now() - this._concentrationSuctionStart) > 1200) {
      this._concentrationSuctionActive = false;
      this._concentrationSuctionStart = 0;
      this._clearStage();
    }

    // If concentration suction is currently playing, hold off rebuilding the DOM until the 480ms suction finishes
    const suctionElapsed = this._concentrationSuctionStart ? (performance.now() - this._concentrationSuctionStart) : 9999;
    const isSuctionHolding = this._concentrationSuctionActive && suctionElapsed < 480;
    const isMeltHolding = this._stageOwner === 'melt-convergence' && cs.meltSeq === this.lastSeenMeltSeq;

    if (!isSuctionHolding && !isMeltHolding && (this._handDirty || !this.lastHandSignature)) {
      this._handDirty = false;
      // Lazy signature generation only when hand actually changed
      let signature = '';
      for (let i = 0; i < hand.length; i++) {
        const c = hand[i];
        signature += c ? `${c.id}_${c.instanceId}|` : 'null|';
      }
      this.lastHandSignature = signature;
      this.buildHandDOM();
    }

    // Concentration or Mental Extraction Spell triggered: play supernova burst, shockwaves, sparks, and deal-in animation
    const isMentalExtractionTriggered = cs.mentalExtractionSeq && cs.mentalExtractionSeq !== this.lastSeenMentalExtractionSeq;
    const isConcentrationTriggered = cs.concentrationSeq && cs.concentrationSeq !== this.lastSeenConcentrationSeq;
    if (isConcentrationTriggered || isMentalExtractionTriggered) {
      if (isConcentrationTriggered) this.lastSeenConcentrationSeq = cs.concentrationSeq;
      if (isMentalExtractionTriggered) this.lastSeenMentalExtractionSeq = cs.mentalExtractionSeq;
      const isMental = !!isMentalExtractionTriggered;
      const lastSlotInfo = isMental ? cs.lastMentalExtractionSlot : cs.lastConcentrationSlot;
      const badgeText = isMental ? 'MIRROR' : 'RECONSIDER';
      if (!lastSlotInfo || lastSlotInfo.team === this.game.myTeam) {
        const slot = lastSlotInfo ? (lastSlotInfo.slot ?? 0) : 0;
        const remainingDelay = Math.max(0, 480 - (this._concentrationSuctionStart ? (performance.now() - this._concentrationSuctionStart) : 480));

        setTimeout(() => {
          this._concentrationSuctionActive = false;
          this._concentrationSuctionStart = 0;
          const myHand = this.game.cardSystem.hands[this.game.myTeam] || [];
          let signature = '';
          for (let i = 0; i < myHand.length; i++) {
            const c = myHand[i];
            signature += c ? `${c.id}_${c.instanceId}|` : 'null|';
            if (this._lastHandCardIds) this._lastHandCardIds[i] = c ? c.id : null;
            if (this._lastHandCardInstances) this._lastHandCardInstances[i] = c ? (c.instanceId ?? null) : null;
          }
          this.lastHandSignature = signature;
          this._handDirty = false;
          this.buildHandDOM();
          this.playConcentrationAnimation(slot, badgeText);
        }, remainingDelay);
      }
    }

    // Drilling just fused 3 adjacent cards into a Melt card (see
    // CardSystem.checkDrilling()) - the signature check above already
    // rebuilt this slot's DOM (its instanceId changed), so the one-shot
    // merge flash is applied to the freshly built element, not a stale one.
    if (cs.meltSeq && cs.meltSeq !== this.lastSeenMeltSeq) {
      this.lastSeenMeltSeq = cs.meltSeq;
      if (cs.lastMeltSlot?.team === this.game.myTeam) {
        const wrapperDiv = this.elHandContainer.children[cs.lastMeltSlot.slot];
        const slotDiv = wrapperDiv?.querySelector('.card-slot');
        if (slotDiv) this.playMeltAnimation(slotDiv);
      }
    }

    // Dynamic pre-fusion resonance and convergence for pending triplet melts
    const pendingMelt = this._getPendingMelt();
    const pendingSlots = pendingMelt?.slots || [];
    const resultSlot = pendingMelt?.resultSlot;

    // A newly scheduled triplet opens the stage once - keyed on the card
    // instances themselves, so a second melt of the same slots later still
    // counts as new, and a re-render of the same pending melt does not.
    const meltKey = pendingMelt ? pendingMelt.instanceIds.join('|') : null;
    if (meltKey !== this._meltStageKey) {
      this._meltStageKey = meltKey;
      if (meltKey) {
        if (typeof Image !== 'undefined' && pendingMelt?.meltCard?.images?.[0]) {
          const img = new Image();
          const isMeltGold = inventory?.isSpecialCardActive ? inventory.isSpecialCardActive(pendingMelt.meltCard.name, 'gold') : (inventory?.hasSpecialCard ? inventory.hasSpecialCard(pendingMelt.meltCard.name, 'gold') : false);
          const goldMeltImg = isMeltGold ? getGoldCardImage(pendingMelt.meltCard.name) : null;
          img.src = getCardThumbUrl(goldMeltImg || pendingMelt.meltCard.images[0]);
          if (img.decode) img.decode().catch(() => {});
        }
        this._stageMeltConvergence(pendingSlots, resultSlot, pendingMelt.timer, pendingMelt);
      } else if (this._stageOwner === 'melt-convergence') {
        // The triplet went away without fusing - one of its cards was
        // played while the timer ran. Only ever clears a stage the
        // convergence still owns: when the melt DOES fuse, the block above
        // has already handed the stage to the slam in this same frame.
        this._clearStage();
      }
    }
    if (pendingMelt && this._stageOwner === 'melt-convergence') {
      this._scrubMeltConvergence(pendingMelt.timer);
    }

    // Update card/buy-button affordability and cost visually
    const effectivePlasma = Math.max(0, (currentPlasma !== undefined ? currentPlasma : this.game.economy.resources[this.game.myTeam].plasma) - (this._optimisticReservedPlasma || 0));
    for (let slotIndex = 0; slotIndex < hand.length; slotIndex++) {
      const card = hand[slotIndex];
      let cached = this._cachedHandSlots ? this._cachedHandSlots[slotIndex] : null;
      let wrapperDiv = cached?.wrapperDiv;
      let slotDiv = cached?.slotDiv;
      let costEl = cached?.costEl;

      if (!wrapperDiv || !slotDiv) {
        wrapperDiv = this.elHandContainer.children[slotIndex];
        if (!wrapperDiv) continue;
        slotDiv = wrapperDiv.querySelector('.card-slot');
        if (!slotDiv) continue;
        costEl = slotDiv.querySelector('.card-cost');
        if (this._cachedHandSlots) {
          cached = { wrapperDiv, slotDiv, costEl, lastCostText: costEl ? costEl.textContent : '' };
          this._cachedHandSlots[slotIndex] = cached;
        }
      }

      // Re-applied every frame rather than set once: buildHandDOM() can
      // replace this whole row mid-performance (a draw resolving, the
      // fusion itself), and the card standing in for a copy on the stage
      // has to keep standing still afterwards.
      const isStaged = this._stageHiddenSlots?.has(slotIndex) === true;
      const isCandidate = !isStaged && pendingSlots.includes(slotIndex);
      const isTarget = isCandidate && slotIndex === resultSlot;
      const isSource = isCandidate && slotIndex !== resultSlot;

      if (cached) {
        if (cached.lastIsStaged !== isStaged) {
          wrapperDiv.classList.toggle('stage-source-hidden', isStaged);
          cached.lastIsStaged = isStaged;
        }
        if (cached.lastIsCandidate !== isCandidate) {
          slotDiv.classList.toggle('card-melting-candidate', isCandidate);
          cached.lastIsCandidate = isCandidate;
          if (!isCandidate) slotDiv.style.removeProperty('--melt-target-x');
        }
        if (cached.lastIsMeltingTarget !== isTarget) {
          slotDiv.classList.toggle('card-melting-target', isTarget);
          cached.lastIsMeltingTarget = isTarget;
        }
        if (cached.lastIsMeltingSource !== isSource) {
          slotDiv.classList.toggle('card-melting-source', isSource);
          cached.lastIsMeltingSource = isSource;
        }
      } else {
        wrapperDiv.classList.toggle('stage-source-hidden', isStaged);
        slotDiv.classList.toggle('card-melting-candidate', isCandidate);
        slotDiv.classList.toggle('card-melting-target', isTarget);
        slotDiv.classList.toggle('card-melting-source', isSource);
        if (!isCandidate) slotDiv.style.removeProperty('--melt-target-x');
      }

      if (card && !card.pending) {
        const cost = this.game.cardSystem.getEffectiveCost(this.game.myTeam, card);
        const canAfford = effectivePlasma >= cost;
        if (cached) {
          if (cached.lastCanAfford !== canAfford) {
            slotDiv.classList.toggle('affordable', canAfford);
            slotDiv.classList.toggle('unaffordable', !canAfford);
            cached.lastCanAfford = canAfford;
          }
          if (costEl) {
            const newCostText = cost > 0 ? `${cost}` : 'FREE';
            if (cached.lastCostText !== newCostText) {
              costEl.textContent = newCostText;
              cached.lastCostText = newCostText;
            }
          }
        } else {
          slotDiv.classList.toggle('affordable', canAfford);
          slotDiv.classList.toggle('unaffordable', !canAfford);
          if (costEl) {
            const newCostText = cost > 0 ? `${cost}` : 'FREE';
            if (costEl.textContent !== newCostText) {
              costEl.textContent = newCostText;
            }
          }
        }
      } else if (!card) {
        const isContracts = !!this.game?.battlefield?.moduleSystem?.hasCongressBuff(this.game?.myTeam, 'government_contracts');
        const drawCost = this.game.cardSystem.getEffectiveDrawCost ? this.game.cardSystem.getEffectiveDrawCost(this.game.myTeam) : this.game.cardSystem.drawCost;
        const canAfford = effectivePlasma >= drawCost;
        if (cached) {
          if (cached.lastCanAfford !== canAfford) {
            slotDiv.classList.toggle('affordable', canAfford);
            slotDiv.classList.toggle('unaffordable', !canAfford);
            cached.lastCanAfford = canAfford;
          }
          if (cached.lastContracts !== isContracts || cached.lastDrawCost !== drawCost) {
            slotDiv.classList.toggle('contracts-free-draw', isContracts);
            const costEl = slotDiv.querySelector('.draw-cost');
            if (costEl) {
              costEl.innerHTML = isContracts ? 'FREE (0)' : (drawCost > 0 ? `${drawCost} ${getIconSvg('plasma', 12)}` : 'FREE');
            }
            cached.lastContracts = isContracts;
            cached.lastDrawCost = drawCost;
          }
        } else {
          slotDiv.classList.toggle('contracts-free-draw', isContracts);
          slotDiv.classList.toggle('affordable', canAfford);
          slotDiv.classList.toggle('unaffordable', !canAfford);
          const costEl = slotDiv.querySelector('.draw-cost');
          if (costEl) {
            costEl.innerHTML = isContracts ? 'FREE (0)' : (drawCost > 0 ? `${drawCost} ${getIconSvg('plasma', 12)}` : 'FREE');
          }
        }
      }
    }
  }

  // Rebuilds only the hand slot(s) whose card identity actually changed
  // since the last call, instead of tearing down and recreating all 5
  // slots (innerHTML='' + full rebuild) on every single hand change (a
  // card play or draw touches at most 2 slots: the one that emptied and
  // the one a new card landed in). Un-rebuilt slots keep their exact DOM
  // node - no lost hover/zoom state, no interrupted in-flight drag or
  // click on an UNRELATED slot when a snapshot resolves a different one
  // mid-gesture (docs/ONLINE_PERFORMANCE_AND_CARD_LAG_ANALYSIS.md Ursache
  // 3). Per-frame affordability/cost updates (updateHandUI()'s own loop
  // below this method) already run independently of this rebuild via the
  // same `_cachedHandSlots` cache, so they are unaffected either way.
  buildHandDOM() {
    const hand = this.game.cardSystem.hands[this.game.myTeam];
    const plasma = Math.max(0, this.game.economy.resources[this.game.myTeam].plasma - (this._optimisticReservedPlasma || 0));
    const prevHand = this._prevHand || [];
    // Race-gated draw-cost pill color (docs/RACE_PLASMA_GAUGE_PARADIGMA.md) -
    // same race-gate as the plasma gauge theme above, so the "Draw Card"
    // slot's plasma pill reads Sul-purple instead of the Human green.
    const isSulTheme = (this.game.battlefield?.bases?.[this.game.myTeam]?.race || 'Human') === 'Sul';

    // Bootstrap / recovery fallback: a mismatched child count means the
    // container is empty (first build) or otherwise out of sync with
    // `hand.length` - safest to start clean rather than trust per-slot
    // indices into a container that doesn't match the hand shape.
    if (this.elHandContainer.children.length !== hand.length) {
      this.elHandContainer.innerHTML = '';
      this._cachedHandSlots = [];
    }
    if (!this._cachedHandSlots) this._cachedHandSlots = [];

    hand.forEach((card, slotIndex) => {
      const casteProgress = this._getCasteProgress(card);
      const duplicateGlowClass = this._getDuplicateGlowClass(card, casteProgress);
      const identityKey = card ? `${card.id}_${card.instanceId}_seg${casteProgress?.segments || 0}_${duplicateGlowClass || ''}` : 'null';
      const cachedEntry = this._cachedHandSlots[slotIndex];
      if (cachedEntry && cachedEntry.identityKey === identityKey) {
        // Same card (or same empty slot) as last build - nothing about
        // this slot's structure needs to change. Leave its DOM node,
        // listeners, and any in-flight interaction on it completely alone.
        return;
      }
      // A new card entered this slot: make sure its unit-specific SFX are
      // already decoded before the unit is played (no-op for most cards).
      if (card?.name) this.game.audio?.warmUnitSamples?.(card.name);

      // The card element the zoom is anchored to (if any) is about to be
      // replaced if it's THIS slot, so a zoom left standing would hang over
      // a card that no longer exists (or, worse, a different card in the
      // same slot). Unaffected slots below never reach this, so hovering
      // one card while a different slot resolves no longer closes the zoom.
      if (this._cardZoomSlot === slotIndex) this.hideCardZoom();

      const wrapperDiv = document.createElement('div');
      wrapperDiv.className = 'card-wrapper';

      // Three layers, one job each - see docs/MOBILE_ANIMATION_PLAN.md §3.
      // .card-wrapper holds the on-screen layout box and the idle float,
      // .card-scale holds NOTHING BUT the size (155x228 times the hand
      // scale), and .card-slot below holds the card itself in design
      // pixels. The split exists because a CSS animation that sets
      // transform REPLACES it: with the scale on .card-slot, every
      // keyframe's scale(1) snapped a phone's card back to its full
      // authored size mid-animation. Now animations are written once, in
      // design pixels, exactly like desktop, and the parent scale carries
      // them - no per-breakpoint keyframe copies.
      const scaleDiv = document.createElement('div');
      scaleDiv.className = 'card-scale';
      wrapperDiv.appendChild(scaleDiv);

      const slotDiv = document.createElement('div');
      slotDiv.className = 'card-slot';
      slotDiv.addEventListener('contextmenu', (e) => e.preventDefault());

      if (!card) {
        // Empty slot doubles as the buy button - a manual DRAW command,
        // no more passive per-second timer. See NEUSTART_PLAN.html Phase 3.
        const isContracts = !!this.game?.battlefield?.moduleSystem?.hasCongressBuff(this.game?.myTeam, 'government_contracts');
        const drawCost = this.game.cardSystem.getEffectiveDrawCost ? this.game.cardSystem.getEffectiveDrawCost(this.game.myTeam) : this.game.cardSystem.drawCost;
        const canAfford = plasma >= drawCost;
        slotDiv.className = `card-slot empty-slot ${canAfford ? 'affordable' : 'unaffordable'}${isSulTheme ? ' sul-plasma-draw' : ''}${isContracts ? ' contracts-free-draw' : ''}`;
        const costText = isContracts ? 'FREE (0)' : (drawCost > 0 ? `${drawCost} ${getIconSvg('plasma', 12)}` : 'FREE');
        slotDiv.innerHTML = `
          <div class="slot-drawing">
            <div class="draw-icon-wrap">
              <span class="draw-icon">${getIconSvg('energy', 22)}</span>
            </div>
            <span class="draw-label">Draw Card</span>
            <div class="draw-cost-badge">
              <span class="draw-cost">${costText}</span>
            </div>
          </div>
        `;

        // A rebuild while this slot is still waiting on its DRAW (a play
        // resolving elsewhere) hands us a brand new node with the "buy"
        // face on it again - put the waiting face back.
        if (this._deployWindowSlots?.has(slotIndex)) {
          const entry = this._deployWindowSlots.get(slotIndex);
          if (entry.kind === 'draw') {
            this._applyDrawWindowVisual(slotDiv);
            entry.slotDiv = slotDiv;
            entry.wrapperDiv = wrapperDiv;
          }
        }

        slotDiv.addEventListener('click', (e) => {
          e.stopPropagation();
          // All the affordability re-check, audio and Draw Window logic
          // lives in drawSlot() now - shared with the 'D' hotkey in
          // js/main.js, see that method's own comment for why.
          this.drawSlot(slotIndex);
        });
        scaleDiv.appendChild(slotDiv);
      } else if (card.pending) {
        // Triplet Melt in-progress placeholder - card definition is still
        // cooking on the server, see startMeltDrill() in
        // js/engine/cardSystem.js.
        slotDiv.className = 'card-slot filled pending-card unaffordable';
        slotDiv.innerHTML = `
          <div class="slot-drawing">
            <div class="draw-icon-wrap">
              <span class="draw-icon">${getIconSvg('hourglass', 22)}</span>
            </div>
            <span class="draw-label">Materializing...</span>
          </div>
        `;
        scaleDiv.appendChild(slotDiv);
      } else {
        const cost = this.game.cardSystem.getEffectiveCost(this.game.myTeam, card);
        const canAfford = plasma >= cost;
        const rarityClass = `rarity-${card.rarity.toLowerCase()}`;
        const raceClass = `race-${(card.race || 'terran').toLowerCase()}`;
        const meltClass = card.type === 'Melt' ? 'card-type-melt' : '';
        const casteClass = this._getCasteClass(card);
        const themeClass = this._getCardThemeClasses();
        const isNewArrival = !prevHand[slotIndex];

        slotDiv.className = `card-slot filled ${rarityClass} ${raceClass} ${meltClass} ${casteClass} ${themeClass} ${canAfford ? 'affordable' : 'unaffordable'} ${isNewArrival ? 'card-draw-enter' : ''}`;
        slotDiv.innerHTML = this.buildCardInnerHTML(card, slotIndex, cost);

        // The duplicate glow (.drilling-duplicate-glow / .caste-duplicate-glow)
        // lives inside .card-inner (embedded by buildCardInnerHTML()), where
        // .card-inner's overflow:hidden and border-radius:4px clip it cleanly
        // to the artwork boundary. It must never be moved out onto .card-scale:
        // .card-scale is the entire 155x228 outer container, so an inset
        // shadow placed there would paint directly over .card-slot.filled's
        // 9px card frame and destroy the frame and rounded corners.

        // A hand rebuild while this slot is still deploying (a draw
        // resolving elsewhere, a melt) hands us a brand new node. It gets
        // the RESTING state, never .card-deploying again: that animation
        // ends the card at opacity 0 and re-adding it would flash the card
        // back in and warp it away a second time.
        if (this._deployWindowSlots?.has(slotIndex)) {
          const entry = this._deployWindowSlots.get(slotIndex);
          if (entry.card && (card.instanceId || card.id) === (entry.card.instanceId || entry.card.id)) {
            slotDiv.classList.add('card-deploy-window');
            wrapperDiv.classList.add('slot-deploying');
            slotDiv.style.pointerEvents = 'none';
            entry.slotDiv = slotDiv;
            entry.wrapperDiv = wrapperDiv;
          }
        }

        scaleDiv.appendChild(slotDiv);

        // Drag-to-play: press and hold (or start moving) a card to lift a
        // full-size, readable preview of it - see _startCardPress() and
        // beginCardDrag() etc. above (shared across all cards, not
        // per-card state - see the comment on _startCardPress). Dragging
        // that preview up into the field plays it; letting go anywhere
        // else cancels and it snaps back. A plain tap that never arms the
        // drag at all still plays instantly via the 'click' listener below.
        slotDiv.addEventListener('pointerdown', (e) => this._startCardPress(e, slotIndex, card, cost, slotDiv));

        // Hover-to-inspect (desktop only - `hover: hover` in CSS terms).
        // Touch deliberately gets nothing here: a touchscreen synthesises
        // a pointerenter on tap, so wiring this for every pointer type
        // would pop a zoom over the hand on the way to simply playing a
        // card. Touch reads a card by holding it instead, which lifts the
        // drag preview at the same zoom size (see beginCardDrag).
        slotDiv.addEventListener('pointerenter', (e) => {
          if (e.pointerType !== 'mouse') return;
          if (!this._canHover() || this._isTouchInteraction()) return;
          this._scheduleCardZoom(slotIndex, card, slotDiv);
        });
        slotDiv.addEventListener('pointerleave', () => this.hideCardZoom());

        // The play-on-tap listener lives on slotDiv itself, NOT on the
        // nested .card-inner it used to be on (from before the drag
        // gesture existed) - slotDiv.setPointerCapture() in
        // _startCardPress() means every subsequent event for that
        // pointer, including the eventual synthetic 'click', gets
        // RETARGETED to the capturing element per the Pointer Events
        // spec. A listener on a descendant of that element (.card-inner)
        // is outside the retargeted event's bubble path and would simply
        // never see it - which reads as "clicking a card does nothing,
        // only drag works" (only on desktop mice: touch's own tap
        // synthesis apparently isn't affected the same way).
        slotDiv.addEventListener('click', (e) => {
          e.stopPropagation();
          // The drag resolution above already handled this same gesture -
          // touch still fires 'click' after pointerup regardless of how
          // long the press lasted or how far it moved, as long as the
          // element under the finger didn't change.
          if (this._suppressClickSlots.has(slotIndex)) {
            this._suppressClickSlots.delete(slotIndex);
            return;
          }

          // The affordability re-check, audio and Deploy Window logic
          // lives in playSlot() now - shared with the '1'-'5' hotkeys in
          // js/main.js, see that method's own comment for why.
          this.playSlot(slotIndex);
        });
      }

      // Replace exactly this slot's node in place (same position, every
      // other slot's node untouched) rather than appending after a blanket
      // clear - existingChild is this same slotIndex's PREVIOUS node when
      // one exists (a real rebuild), or undefined on first build/bootstrap.
      const existingChild = this.elHandContainer.children[slotIndex];
      if (existingChild) {
        this.elHandContainer.replaceChild(wrapperDiv, existingChild);
      } else {
        this.elHandContainer.appendChild(wrapperDiv);
      }
      const costEl = slotDiv.querySelector('.card-cost');
      this._cachedHandSlots[slotIndex] = {
        identityKey,
        wrapperDiv,
        slotDiv,
        costEl,
        lastCostText: costEl ? costEl.textContent : ''
      };
    });

    // A freshly rebuilt slot (see the identityKey skip above - an
    // untouched slot's wrapper already carries whatever hidden/visible
    // state it had before, nothing to redo) may have replaced the node
    // that was hiding the origin of a card currently held in the air,
    // which would leave that card on screen twice: big in the lift and
    // small in the hand. Rebuilds during a drag are not exotic: holding a
    // card is how it gets read on touch, so a queued draw resolving
    // mid-hold is routine. Only re-hide when the slot still holds the
    // very card being held; if the drag's card has moved or gone, the
    // lift is stale and the fresh hand is what's true.
    const held = this._cardDrag;
    if (held?.armed) {
      const liveCard = hand[held.slotIndex];
      const sameCard = liveCard && held.card
        && (liveCard.instanceId || liveCard.id) === (held.card.instanceId || held.card.id);
      const wrapper = this.elHandContainer.children[held.slotIndex];
      const slotDiv = wrapper?.querySelector('.card-slot');
      if (sameCard && wrapper && slotDiv) {
        wrapper.classList.add('drag-source-hidden');
        held.slotDiv = slotDiv; // the node the drag was holding is detached now
      }
    }

    this._prevHand = hand.map(c => (c ? (c.instanceId || c.id) : null));

    // Forces a synchronous reflow right after this call's slot replacements -
    // see #cards-hand-container's translateZ(0) comment in css/main.css: a
    // known mobile Safari/WebKit bug can size a fresh height:auto flex item
    // wrong on its very first paint, only correcting once something forces
    // a reflow. buildHandDOM() runs on every hand change (draw/play/melt),
    // so a freshly replaced empty "Draw Card" slot is a fresh first-paint
    // element each time - previously this only ever got fixed by accident,
    // whenever some unrelated interaction (e.g. shakeInsufficientFunds's own
    // offsetWidth read) happened to force a reflow first. Reading it here
    // directly guarantees correct sizing immediately, instead of leaving
    // the slot visibly shrunken until some other event happens to paper
    // over it. Unconditional even when this call only touched one slot -
    // cheap relative to the DOM mutation that just happened either way.
    void this.elHandContainer.offsetHeight;

    // The reflow above only settles the BROWSER's own layout - it does
    // nothing to the --hand-card-w/-h/-scale custom properties themselves,
    // which updateHandCardScale() computes from container.clientWidth once
    // and only re-reads on an actual resize/orientationchange/ResizeObserver
    // firing (see initElements()). None of those fire from a hand-content
    // change alone (the container's own box never resizes just because a
    // card was drawn/played), so a scale computed from a too-early/unsettled
    // width - the same first-paint risk the reflow above targets - stayed
    // wrong for every subsequent card, including freshly emptied "Draw
    // Card" slots, until some unrelated resize happened to correct it. This
    // is the "Nachzieh-Flächen sind zu klein" report: recomputing right
    // here, against the now-reflowed layout, makes every hand rebuild
    // self-correct instead of only sometimes doing so by accident.
    this.updateHandCardScale();
  }

  init2v2(matchData) {
    this.is2v2 = true;
    this.myTeam = matchData?.team || this.game?.myTeam || 'western';
    const dock = document.getElementById('hud-teammate-dock');
    if (dock) {
      dock.classList.add('hidden');
      dock.setAttribute?.('aria-hidden', 'true');
    }
    this.updateTeammateDock(matchData?.teammatePlasma ?? 20, matchData?.teammateHand || []);
  }

  hideTeammateDock() {
    if (this.game?.is2v2) return;
    this.is2v2 = false;
    const dock = document.getElementById('hud-teammate-dock');
    if (dock) {
      dock.classList.add('hidden');
      dock.setAttribute?.('aria-hidden', 'true');
    }
    const westWrap = document.getElementById('western-teammate-plasma-wrap');
    const eastWrap = document.getElementById('eastern-teammate-plasma-wrap');
    if (westWrap) westWrap.classList.add('hidden');
    if (eastWrap) eastWrap.classList.add('hidden');
    document.getElementById('western-teammate-plasma-fill')?.classList.remove('plasma-full');
    document.getElementById('eastern-teammate-plasma-fill')?.classList.remove('plasma-full');
    document.getElementById('western-teammate-plasma-text')?.classList.remove('plasma-full');
    document.getElementById('eastern-teammate-plasma-text')?.classList.remove('plasma-full');
  }

  updateTeammateDock(plasma = 0, hand = []) {
    if (!this.is2v2 && !this.game?.is2v2) return;
    this.is2v2 = true;
    const curPlasma = Math.max(0, Math.min(150, Math.floor(plasma)));
    const pct = Math.min(100, (curPlasma / 150) * 100);
    const isFull = curPlasma >= 150;

    const team = this.myTeam || this.game?.myTeam || 'western';
    const fillEl = document.getElementById(`${team}-teammate-plasma-fill`);
    const textEl = document.getElementById(`${team}-teammate-plasma-text`);

    if (fillEl) {
      fillEl.style.width = `${pct}%`;
      fillEl.classList.toggle('plasma-full', isFull);
    }
    if (textEl) {
      textEl.textContent = `ALLY ${curPlasma}`;
      textEl.classList.toggle('plasma-full', isFull);
    }

    const slotsEl = document.getElementById('teammate-hand-slots');
    if (slotsEl && Array.isArray(hand)) {
      const slots = slotsEl.querySelectorAll('.teammate-card-slot');
      for (let i = 0; i < 5; i++) {
        const slotDiv = slots[i];
        if (!slotDiv) continue;
        const card = hand[i];
        if (card && card.name && card.name !== '···') {
          const targetClass = `teammate-card-slot filled ${card.race === 'Sul' ? 'race-sul' : 'race-human'}`;
          if (slotDiv.className !== targetClass) slotDiv.className = targetClass;

          let costSpan = slotDiv.querySelector('.mini-cost');
          let nameSpan = slotDiv.querySelector('.mini-name');
          if (!costSpan || !nameSpan) {
            slotDiv.innerHTML = '';
            costSpan = document.createElement('span');
            costSpan.className = 'mini-cost';
            nameSpan = document.createElement('span');
            nameSpan.className = 'mini-name';
            slotDiv.appendChild(costSpan);
            slotDiv.appendChild(nameSpan);
          }
          const costText = String(card.cost ?? '');
          if (costSpan.textContent !== costText) costSpan.textContent = costText;
          if (nameSpan.textContent !== card.name) nameSpan.textContent = card.name;
          const titleText = `${card.name} (${card.cost ?? 0} Plasma)`;
          if (slotDiv.title !== titleText) slotDiv.title = titleText;
        } else {
          if (slotDiv.className !== 'teammate-card-slot empty') {
            slotDiv.className = 'teammate-card-slot empty';
            slotDiv.innerHTML = '';
            slotDiv.title = 'Empty Slot';
          }
        }
      }
    }
  }
}
